import { useState } from 'react';
import {
  BookOpen,
  DollarSign,
  GitBranch,
  CheckSquare,
  TrendingUp,
  ChevronDown,
  ChevronRight,
  FileText,
  Users2,
  Building2,
  Clock,
  AlertCircle,
  Lightbulb,
  ExternalLink,
  Scale,
  Shield,
  AlertTriangle,
  MessageCircle,
  Edit2,
  LogOut,
  ShieldCheck,
  BarChart3,
  ArrowUpRight,
  ArrowDownRight,
} from 'lucide-react';

// Exit Scenarios data - based on Vigil-Amgen MAB Program Termination
const exitScenarios = {
  triggers: [
    {
      id: 'convenience',
      name: 'Termination for Convenience',
      description: 'Either party terminates without cause, typically with notice period',
      notice: '90-180 days typical',
      color: 'blue',
    },
    {
      id: 'cause',
      name: 'Termination for Cause',
      description: 'Material breach not cured within cure period',
      notice: '30-60 day cure period',
      color: 'red',
    },
    {
      id: 'manda',
      name: 'Change of Control / M&A',
      description: 'Triggered by merger, acquisition, or change in ownership',
      notice: 'Upon closing or shortly after',
      color: 'purple',
    },
    {
      id: 'safety',
      name: 'Safety / Regulatory',
      description: 'Serious safety issue or regulatory action',
      notice: 'Immediate or very short',
      color: 'orange',
    },
  ],
  checklist: [
    {
      category: 'IP & Technology',
      items: [
        { item: 'Patent rights assignment or reversion', critical: true },
        { item: 'Know-how transfer or return', critical: true },
        { item: 'License-back for continuing programs', critical: false },
        { item: 'Trademark rights disposition', critical: false },
      ],
    },
    {
      category: 'Clinical Trials',
      items: [
        { item: 'Ongoing trial: transfer vs wind-down decision', critical: true },
        { item: 'Election deadline (typically 10 business days)', critical: true },
        { item: 'Wind-down costs responsibility', critical: false },
        { item: 'Patient safety and ethical obligations', critical: true },
      ],
    },
    {
      category: 'Regulatory',
      items: [
        { item: 'IND/NDA/BLA transfer or closure', critical: true },
        { item: 'Right of reference grant', critical: false },
        { item: 'Pharmacovigilance transition', critical: true },
        { item: 'Regulatory filing deadlines', critical: false },
      ],
    },
    {
      category: 'Data & Materials',
      items: [
        { item: 'Clinical data transfer', critical: true },
        { item: 'Manufacturing know-how return', critical: false },
        { item: 'Biological materials return', critical: false },
        { item: 'Confidential information handling', critical: true },
      ],
    },
    {
      category: 'Financial',
      items: [
        { item: 'Accrued payment obligations', critical: true },
        { item: 'Milestone payment adjustments', critical: false },
        { item: 'Royalty tail on terminated products', critical: false },
        { item: 'Wind-down cost allocation', critical: true },
      ],
    },
    {
      category: 'Personnel & Transition',
      items: [
        { item: 'Key personnel transition period', critical: false },
        { item: 'Non-solicitation provisions', critical: false },
        { item: 'Consulting arrangements', critical: false },
        { item: 'Training and knowledge transfer', critical: false },
      ],
    },
  ],
  realCase: {
    title: 'Vigil-Amgen MAB Program Partial Termination (2025)',
    context: 'Vigil terminated MAB Program due to merger with third party; Small Molecule Program continued',
    keyTerms: [
      { term: 'Termination Effective Date', value: 'Contingent on Merger closing' },
      { term: 'Clinical Trial Decision', value: 'Amgen has 10 business days to decide transfer or wind-down' },
      { term: 'IP Assignment', value: 'All MAB-related patents assigned to Amgen' },
      { term: 'License-Back', value: 'Non-exclusive license for "Agreement Technology" to Amgen' },
      { term: 'Regulatory Filings', value: 'All MAB-related filings assigned to Amgen' },
      { term: 'Royalties', value: 'Vigil waived all royalties on Terminated Product' },
      { term: 'Clinical Data', value: 'Vigil to provide Phase 1 readout to Amgen' },
      { term: 'Surviving Programs', value: 'Small Molecule Program continues unchanged' },
    ],
  },
};

// China CSO Compliance data - based on RDPAC 2024 Guidance
const chinaComplianceData = {
  overview: {
    title: 'CSO Compliance in China',
    issuer: 'RDPAC (中国外商投资企业协会药品研制和开发工作委员会)',
    date: 'October 2024',
    description: 'Guidelines for pharma companies working with Contract Sales Organizations (CSO) in China',
  },
  keyRisks: [
    { risk: 'Bribery liability', description: 'Drug owner may be liable for CSO\'s bribery under FCPA and Chinese law', severity: 'high' },
    { risk: 'Off-label promotion', description: 'CSO may promote products for unapproved indications', severity: 'high' },
    { risk: 'Improper HCP payments', description: 'Speaker fees, consulting fees, gifts to healthcare professionals', severity: 'high' },
    { risk: 'Books & records', description: 'Inaccurate expense recording and reimbursement', severity: 'medium' },
    { risk: 'Antitrust issues', description: 'Price fixing, market allocation with distributors', severity: 'medium' },
  ],
  checklist: [
    {
      id: 'due-diligence',
      title: '1. Pre-Engagement Due Diligence',
      items: [
        'Verify CSO\'s registered business scope covers planned activities',
        'Assess CSO\'s internal compliance program adequacy',
        'Review CSO\'s past anti-bribery law violations (if any)',
        'Evaluate compliance awareness of CSO leadership',
        'Check CSO compliance function independence from sales team',
      ],
      tips: 'If past violations found, assess: (1) systemic weakness? (2) relevance to planned activities? (3) same personnel involved?',
    },
    {
      id: 'contract-clauses',
      title: '2. CSO Agreement Compliance Clauses',
      items: [
        'Require adherence to China anti-bribery laws',
        'Require adherence to FCPA (if applicable)',
        'Require compliance with RDPAC Code of Practice',
        'Grant audit and monitoring rights',
        'Include termination right for significant violations',
      ],
      tips: 'CSO should comply with its own policies AND your compliance policies',
    },
    {
      id: 'service-fees',
      title: '3. Service Fees: Fair Market Value',
      items: [
        'Cost-plus mechanism: cost + reasonable profit margin',
        'Performance-based: tied to quantitative indicators',
        'Verify services before payment',
        'Evaluate incentive effects on CSO behavior',
        'Document FMV determination methodology',
      ],
      tips: 'Avoid fee structures that incentivize improper promotional activities',
    },
    {
      id: 'monitoring',
      title: '4. Ongoing Monitoring & Audit',
      items: [
        'Review CSO activity reports regularly',
        'Inspect CSO representative activities (with/without notice)',
        'Review and test CSO expenses',
        'Interview CSO personnel periodically',
        'Conduct on-site audits',
      ],
      tips: 'Can be done by: (1) your team, (2) CSO self-audit with reporting, or (3) third party',
    },
    {
      id: 'training',
      title: '5. Compliance Training',
      items: [
        'Train CSO on compliance requirements annually',
        'Cover Chinese laws and applicable foreign laws (FCPA)',
        'Cover RDPAC Code requirements',
        'Administer compliance exams to CSO medical reps',
        'Maintain training records',
      ],
      tips: 'Make exams mandatory before CSO personnel can start activities',
    },
    {
      id: 'reporting',
      title: '6. Reporting & Remediation',
      items: [
        'Set up compliance hotline for CSO transactions',
        'Require CSO to report violations discovered',
        'Develop remediation plans for minor violations',
        'Terminate CSO Agreement for severe violations',
        'Document all compliance incidents and actions',
      ],
      tips: 'Hotline should be available to both your personnel and CSO personnel',
    },
  ],
  csoActivities: [
    'Product detailing to HCPs',
    'Medical/scientific education of HCPs',
    'Product education/training of HCPs',
    'Disease education (patients/public)',
    'Market access services (hospital listing)',
    'Market intelligence gathering',
  ],
};

// Deal Benchmarks Data - from 443 signals with 169 having deal values
const dealBenchmarksData = {
  summary: {
    totalDeals: 443,
    dealsWithValue: 169,
    avgUpfront: 158,
    avgTotal: 980,
    timeframe: '2022-2025',
  },
  byTherapeuticArea: [
    { area: 'Oncology', deals: 142, avgUpfront: 180, avgTotal: 1200, maxUpfront: 1250, trend: 'up' },
    { area: 'Immunology', deals: 29, avgUpfront: 150, avgTotal: 950, maxUpfront: 800, trend: 'up' },
    { area: 'Metabolic/MASH', deals: 22, avgUpfront: 200, avgTotal: 1100, maxUpfront: 600, trend: 'up' },
    { area: 'Neurology/CNS', deals: 14, avgUpfront: 120, avgTotal: 800, maxUpfront: 400, trend: 'stable' },
    { area: 'Cardiovascular', deals: 9, avgUpfront: 100, avgTotal: 650, maxUpfront: 300, trend: 'stable' },
    { area: 'Rare Disease', deals: 8, avgUpfront: 80, avgTotal: 500, maxUpfront: 250, trend: 'up' },
    { area: 'Infectious Disease', deals: 5, avgUpfront: 60, avgTotal: 400, maxUpfront: 150, trend: 'down' },
  ],
  byModality: [
    { modality: 'ADC', deals: 28, avgUpfront: 250, avgTotal: 1800, premiumVsAvg: '+58%', hot: true },
    { modality: 'Bispecific Antibody', deals: 22, avgUpfront: 200, avgTotal: 1400, premiumVsAvg: '+33%', hot: true },
    { modality: 'Small Molecule', deals: 85, avgUpfront: 100, avgTotal: 600, premiumVsAvg: '-23%', hot: false },
    { modality: 'mRNA/LNP', deals: 15, avgUpfront: 180, avgTotal: 1100, premiumVsAvg: '+12%', hot: true },
    { modality: 'Cell Therapy', deals: 18, avgUpfront: 150, avgTotal: 900, premiumVsAvg: '-2%', hot: false },
    { modality: 'Gene Therapy/AAV', deals: 12, avgUpfront: 130, avgTotal: 850, premiumVsAvg: '-12%', hot: false },
    { modality: 'Radiopharmaceutical', deals: 8, avgUpfront: 220, avgTotal: 1500, premiumVsAvg: '+47%', hot: true },
  ],
  recentMegaDeals: [
    { buyer: 'GSK', seller: 'Hengrui Medicine', upfront: 1250, total: 12500, area: 'Immunology', modality: 'PDE3/4 Inhibitor', date: '2025-07' },
    { buyer: 'BMS', seller: 'BioNTech (Pumigen)', upfront: 800, total: 11100, area: 'Oncology', modality: 'PD-L1/VEGF Bispecific', date: '2025-06' },
    { buyer: 'AstraZeneca', seller: 'CSPC Pharma', upfront: 100, total: 5330, area: 'Oncology', modality: 'Undisclosed', date: '2025' },
    { buyer: 'Vertex', seller: 'Orna Therapeutics', upfront: 65, total: 4080, area: 'Hematology', modality: 'mRNA/LNP', date: '2025-01' },
    { buyer: 'GSK', seller: 'ABL Bio', upfront: 50, total: 2700, area: 'CNS', modality: 'BBB Shuttle Platform', date: '2025-04' },
  ],
  upfrontRatios: [
    { stage: 'Preclinical', avgRatio: '5-10%', range: '$10-50M', note: 'Higher for validated targets' },
    { stage: 'Phase 1', avgRatio: '8-15%', range: '$30-150M', note: 'Safety data premium' },
    { stage: 'Phase 2', avgRatio: '15-25%', range: '$100-500M', note: 'Efficacy signal critical' },
    { stage: 'Phase 3', avgRatio: '25-40%', range: '$300M-1B+', note: 'De-risked, premium pricing' },
    { stage: 'Approved', avgRatio: '40-60%', range: '$500M-5B+', note: 'Commercial value proven' },
  ],
  insights: [
    { icon: 'trend', text: 'ADC deals command 58% premium over average - driven by Daiichi Sankyo success', type: 'positive' },
    { icon: 'trend', text: 'Radiopharmaceutical deals surging - 47% above average after Novartis acquisitions', type: 'positive' },
    { icon: 'alert', text: 'Small molecule deals declining in value - commoditization pressure', type: 'warning' },
    { icon: 'info', text: 'China-origin assets typically see 20-30% discount for ex-China rights only', type: 'neutral' },
    { icon: 'trend', text: 'Platform technology deals (AI, delivery) rising - Orna, Dyno examples', type: 'positive' },
  ],
};

// Mock Amendment Document with annotations
const amendmentDocument = {
  title: 'AMENDMENT NO. 2',
  subtitle: 'TO THE COLLABORATION AGREEMENT',
  parties: 'Between Company A, Inc. and Company B Corporation',
  sections: [
    {
      id: 'preamble',
      title: 'PREAMBLE',
      content: `This Amendment No. 2 to the Collaboration Agreement (this "Amendment") is entered into as of the 15th day of March, 2024 (the "Amendment Effective Date") by and between Company A, Inc., a Delaware corporation with a place of business at [Address] ("Company A"), and Company B Corporation, a Delaware corporation with a place of business at [Address] ("Company B"). Company A and Company B are sometimes referred to herein individually as a "Party" and collectively as the "Parties".`,
      annotations: [
        {
          id: 'amendment-effective-date',
          term: 'Amendment Effective Date',
          type: 'info',
          title: 'Amendment Effective Date',
          importance: 'Medium - Sets when changes take effect',
          tips: [
            'Can be signing date or a future date',
            'Consider alignment with fiscal quarters',
            'May be tied to closing of another transaction',
          ],
          example: 'Common: signing date; Conditional: "upon closing of the Merger"',
        },
      ],
    },
    {
      id: 'recitals',
      title: 'RECITALS',
      content: `WHEREAS, Company A and Company B entered into that certain Collaboration Agreement, dated as of January 15, 2022, as amended by Amendment No. 1 to the Collaboration Agreement, dated July 1, 2023 (collectively, the "Agreement"); and

WHEREAS, the Parties have been having discussions regarding the post-termination financial rights and obligations of the Parties related to Product X, a Terminated Product that was terminated pursuant to the termination notice Company A delivered to Company B on December 1, 2023; and

WHEREAS, each of Company A and Company B desire to resolve their disagreements related to the post-termination rights for Product X by further amending certain portions of the Agreement and releasing each other with respect to any existing and potential claims;

NOW, THEREFORE, in consideration of the mutual promises and covenants hereinafter set forth, the Parties agree to amend the Agreement as follows. Capitalized terms used in this Amendment and not otherwise defined herein shall have the meanings ascribed to such terms in the Agreement.`,
      annotations: [
        {
          id: 'whereas-chain',
          term: 'WHEREAS',
          type: 'info',
          title: 'Recitals / Whereas Clauses',
          importance: 'Medium - Provides context and intent',
          tips: [
            'Establishes background and purpose of amendment',
            'Can be used to interpret ambiguous provisions',
            'Should accurately reflect the situation',
          ],
          redFlag: 'Inaccurate recitals can be used against you later',
          example: 'Always review recitals carefully - they tell the "story" of why this amendment exists',
        },
        {
          id: 'prior-amendments',
          term: 'as amended by Amendment No. 1',
          type: 'warning',
          title: 'Prior Amendment Reference',
          importance: 'High - Ensures complete amendment history',
          tips: [
            'List ALL prior amendments in chronological order',
            'Missing amendments can create ambiguity',
            'Confirm you have copies of all referenced amendments',
          ],
          redFlag: 'Incomplete amendment chain can void provisions',
          example: 'Best practice: "as amended by Amendment No. 1 dated [X], Amendment No. 2 dated [Y]..."',
        },
        {
          id: 'capitalized-terms',
          term: 'Capitalized terms used in this Amendment',
          type: 'info',
          title: 'Defined Terms Reference',
          importance: 'Standard - Links to original definitions',
          tips: [
            'Ensures consistency with original agreement',
            'New definitions in amendment override originals',
            'Be careful with modified definitions',
          ],
          example: 'Standard boilerplate - but always check if any terms are redefined in this amendment',
        },
      ],
    },
    {
      id: 'article1',
      title: 'ARTICLE 1 - AMENDMENT TO DEFINITIONS',
      content: `1.1 Amendment to Definition of "FTE Rate". The Parties hereby agree that, following the Amendment Effective Date, the definition of "FTE Rate" in Section 1.45 of the Agreement shall be deleted in its entirety and replaced with the following:

"FTE Rate" means, until such time as the Parties agree otherwise, (A) for activities in the U.S., $[***] per FTE and (B) for activities in other geographic locations, the applicable Blended Rate as set forth in Schedule A. The FTE Rate shall be increased by [***]% annually.

1.2 Additional Definitions. From and after the Amendment Effective Date, the Parties hereby agree that the following definitions are hereby added to the Agreement:

"Blended Rates" means the rates set forth on Schedule A, which shall be reviewed and adjusted annually.

"Global Commercial Personnel" means any person performing global above-market commercial activities, including global marketing, insights, market access, and other functions.`,
      annotations: [
        {
          id: 'deleted-replaced',
          term: 'deleted in its entirety and replaced',
          type: 'critical',
          title: 'Full Replacement Language',
          importance: 'Critical - Complete replacement vs. partial edit',
          tips: [
            'Use "deleted and replaced" for complete rewrites',
            'Use "amended to add/delete" for partial changes',
            'Always specify exact section being modified',
          ],
          redFlag: 'Partial edits can leave conflicting language',
          example: 'Best practice: Replace entire definition to avoid ambiguity',
        },
        {
          id: 'fte-rate',
          term: 'FTE Rate',
          type: 'warning',
          title: 'FTE Rate Changes',
          importance: 'High - Directly impacts cost calculations',
          tips: [
            'Trace all places FTE Rate is used in Agreement',
            'Consider impact on profit/loss sharing',
            'Annual escalation is common (typically 2-4%)',
          ],
          benchmark: 'Typical US FTE Rate: $250K-$400K',
          example: 'Amgen-AZ: Modified FTE Rate multiple times to reflect actual costs',
        },
        {
          id: 'additional-defs',
          term: 'Additional Definitions',
          type: 'info',
          title: 'Adding New Definitions',
          importance: 'Medium - Expands agreement scope',
          tips: [
            'New definitions should not conflict with existing ones',
            'Consider alphabetical placement',
            'Cross-reference to ensure consistency',
          ],
          example: 'Common when adding new categories of personnel or activities',
        },
      ],
    },
    {
      id: 'article2',
      title: 'ARTICLE 2 - AMENDMENT TO FINANCIAL TERMS',
      content: `2.1 Buyout of Post-Termination Rights. Company B shall hereby buyout and purchase Company A out of its post-termination financial payment rights related to Product X under the Agreement.

2.2 Buyout Consideration. Company A's post-termination financial rights under the Agreement related to Product X shall solely consist of:

    (a) $[***] within ten (10) days of the Amendment Effective Date;

    (b) $[***] within sixty (60) days of receipt of Regulatory Approval for Product X for the Primary Indication; and

    (c) Royalties on Net Sales of Product X consisting of:
        (i) [***]% royalty as the Inventorship Margin; plus
        (ii) an additional [***]% royalty payable on a country-by-country basis until the tenth (10th) anniversary of first commercial sale.

2.3 Payment Terms. Such royalties will be payable quarterly within thirty (30) days of the end of the applicable calendar quarter. Each royalty payment will be accompanied by a report setting forth calculation of Net Sales and royalties owed.`,
      annotations: [
        {
          id: 'buyout',
          term: 'buyout and purchase',
          type: 'critical',
          title: 'Buyout Transaction',
          importance: 'Critical - Terminates ongoing rights for lump sum',
          tips: [
            'Carefully value future rights being given up',
            'Consider NPV of expected future payments',
            'Include milestone payments if product succeeds',
          ],
          redFlag: 'Undervaluing buyout price loses long-term value',
          example: 'Typical: Upfront + milestones + reduced royalty',
        },
        {
          id: 'buyout-consideration',
          term: 'Buyout Consideration',
          type: 'critical',
          title: 'Buyout Payment Structure',
          importance: 'Critical - What you receive for giving up rights',
          tips: [
            'Upfront payment provides certainty',
            'Milestones tied to regulatory success',
            'Royalties provide ongoing participation',
          ],
          benchmark: 'Structure varies: 50% upfront / 50% milestones is common',
          example: 'AZ-Amgen Brodalumab: Upfront + Approval Milestone + Tiered Royalties',
        },
        {
          id: 'inventorship-margin',
          term: 'Inventorship Margin',
          type: 'warning',
          title: 'Inventorship Margin Royalty',
          importance: 'High - Compensates for patent contribution',
          tips: [
            'Typically 1-3% for patent contribution',
            'May survive longer than standard royalties',
            'Tied to ownership of key patents',
          ],
          benchmark: 'Typical range: 1-3%',
          example: 'Survives even after buyout in many cases',
        },
        {
          id: 'quarterly-payment',
          term: 'payable quarterly within thirty (30) days',
          type: 'info',
          title: 'Payment Timing',
          importance: 'Medium - Cash flow consideration',
          tips: [
            '30-45 days is standard for pharma',
            'Can negotiate 50-60 days for complex calculations',
            'Include late payment interest provisions',
          ],
          example: 'Amgen-AZ: Modified from 30 to 50 days for certain products',
        },
      ],
    },
    {
      id: 'article3',
      title: 'ARTICLE 3 - RELEASE',
      content: `3.1 Mutual Release. The Parties, on behalf of themselves, their respective parents, subsidiaries and affiliates, and their respective employees, officers, directors, shareholders, agents, attorneys, predecessors, successors, assigns and other representatives:

    (a) hereby release and forever discharge each other from any and all actions, causes of action, suits, claims, judgments, demands, obligations or liabilities, damages, costs, and expenses (including attorneys' fees), in law or equity, whether now known or unknown, arising out of or related to any post-termination financial rights under Section 14.6.1.6 of the Agreement related to Product X; and

    (b) hereby agree not to assert any such released claims against the other Party.

3.2 California Civil Code Waiver. The Parties expressly waive any rights under California Civil Code Section 1542, which provides: "A general release does not extend to claims which the creditor does not know or suspect to exist in his or her favor at the time of executing the release."

3.3 Compromise. This Amendment constitutes a compromise of disputed claims and shall not be deemed an admission of liability by either Party.`,
      annotations: [
        {
          id: 'mutual-release',
          term: 'release and forever discharge',
          type: 'danger',
          title: 'Mutual Release of Claims',
          importance: 'Critical - Gives up right to sue',
          tips: [
            'Carefully scope what claims are being released',
            'Exclude claims under THIS amendment',
            'Consider carve-outs for fraud, willful misconduct',
          ],
          redFlag: 'Overly broad release gives up valid claims',
          example: 'Always limit release to specific disputed matters',
        },
        {
          id: 'known-unknown',
          term: 'known or unknown',
          type: 'danger',
          title: 'Unknown Claims Waiver',
          importance: 'High Risk - Releases claims you don\'t know about',
          tips: [
            'Standard in settlement agreements',
            'Conduct thorough diligence before signing',
            'Consider limiting to "reasonably discoverable" claims',
          ],
          redFlag: 'May release valuable claims discovered later',
          example: 'California 1542 waiver is required to release unknown claims',
        },
        {
          id: 'civil-code-1542',
          term: 'California Civil Code Section 1542',
          type: 'warning',
          title: 'California 1542 Waiver',
          importance: 'High - Required for complete release',
          tips: [
            'California law protects against releasing unknown claims',
            'Must expressly waive to release unknown claims',
            'Other states have similar provisions',
          ],
          example: 'Standard boilerplate for any release with California nexus',
        },
        {
          id: 'compromise',
          term: 'compromise of disputed claims',
          type: 'info',
          title: 'Non-Admission Clause',
          importance: 'Medium - Prevents use as admission',
          tips: [
            'Protects against using settlement as evidence',
            'Standard in dispute resolution',
            'Important for reputational protection',
          ],
          example: 'Standard protective language in any settlement',
        },
      ],
    },
    {
      id: 'article4',
      title: 'ARTICLE 4 - REFERENCE TO AND EFFECT ON AGREEMENT',
      content: `4.1 Reference to Agreement. Upon and after the effectiveness of this Amendment, each reference in the Agreement to "this Agreement", "hereunder", "hereof" or words of like import referring to the Agreement shall mean and be a reference to the Agreement as modified and amended hereby.

4.2 Effectiveness of Amendment. Upon execution and delivery of this Amendment by both Parties, the amendments set forth above shall be effective as of the Amendment Effective Date. Except as specifically amended above, the Agreement is and shall continue to be in full force and effect and is hereby in all respects ratified and confirmed and shall constitute the legal, valid, binding and enforceable obligations of the Parties.

4.3 Conflict. In the event of any conflict between the terms of this Amendment and the Agreement, the terms of this Amendment shall control.`,
      annotations: [
        {
          id: 'reference-clause',
          term: 'each reference in the Agreement',
          type: 'info',
          title: 'Integration Clause',
          importance: 'Standard - Ensures amendment is part of agreement',
          tips: [
            'Standard boilerplate',
            'Ensures future references include amendment',
            'Critical for interpreting as unified document',
          ],
          example: 'Required in every amendment',
        },
        {
          id: 'ratified-confirmed',
          term: 'ratified and confirmed',
          type: 'info',
          title: 'Ratification Language',
          importance: 'Standard - Confirms unamended terms survive',
          tips: [
            'Confirms original agreement still in effect',
            'Only specified changes are made',
            'Prevents argument that amendment voids original',
          ],
          example: 'Critical language - ensures you only change what you intend to',
        },
        {
          id: 'conflict-provision',
          term: 'terms of this Amendment shall control',
          type: 'warning',
          title: 'Conflict Resolution',
          importance: 'High - Determines which document wins',
          tips: [
            'Later amendment should control',
            'Explicit provision avoids ambiguity',
            'Consider order of precedence if multiple amendments',
          ],
          example: 'Best practice: explicitly state amendment controls',
        },
      ],
    },
    {
      id: 'article5',
      title: 'ARTICLE 5 - MISCELLANEOUS',
      content: `5.1 Governing Law. This Amendment will be governed by, and enforced and construed in accordance with, the laws of the State of New York without regard to its conflicts of law provisions.

5.2 Jurisdiction. Each of the Parties hereby irrevocably consents to submit to the exclusive jurisdiction of the state and federal courts of the State of New York for any matter arising out of this Amendment.

5.3 Counterparts. This Amendment may be executed in two (2) or more counterparts, each of which shall be deemed an original, but all of which together shall constitute one and the same instrument. Electronic signatures shall be treated as original signatures.

5.4 Headings. The headings in this Amendment are for convenience of reference only and shall not affect interpretation.`,
      annotations: [
        {
          id: 'governing-law',
          term: 'laws of the State of New York',
          type: 'info',
          title: 'Governing Law',
          importance: 'Medium - Determines legal framework',
          tips: [
            'Should match original agreement',
            'NY and Delaware most common for pharma',
            'Affects interpretation of ambiguous terms',
          ],
          example: 'Standard: Use same governing law as original agreement',
        },
        {
          id: 'exclusive-jurisdiction',
          term: 'exclusive jurisdiction',
          type: 'warning',
          title: 'Exclusive Jurisdiction',
          importance: 'High - Where disputes must be filed',
          tips: [
            'Exclusive means ONLY these courts',
            'Consider convenience for your company',
            'May affect enforceability in other jurisdictions',
          ],
          example: 'Match original agreement; consider arbitration alternative',
        },
        {
          id: 'electronic-signatures',
          term: 'Electronic signatures',
          type: 'info',
          title: 'Electronic Signature Validity',
          importance: 'Medium - Confirms e-signatures valid',
          tips: [
            'Standard post-COVID',
            'DocuSign, Adobe Sign widely accepted',
            'Some jurisdictions have specific requirements',
          ],
          example: 'Modern standard - explicitly state e-signatures are valid',
        },
      ],
    },
    {
      id: 'signatures',
      title: 'SIGNATURES',
      content: `IN WITNESS WHEREOF, duly authorized representatives of the Parties hereto have executed this Amendment as of the date first set forth above.

COMPANY A, INC.                           COMPANY B CORPORATION

By: _______________________               By: _______________________
Name: [Name]                              Name: [Name]
Title: [Title]                            Title: [Title]
Date: _______________                     Date: _______________`,
      annotations: [
        {
          id: 'duly-authorized',
          term: 'duly authorized representatives',
          type: 'warning',
          title: 'Signatory Authority',
          importance: 'High - Invalid signature voids amendment',
          tips: [
            'Confirm signer has authority to bind company',
            'Check corporate bylaws for signing authority',
            'Officers typically have authority; others need board approval',
          ],
          redFlag: 'Unsigned by unauthorized person = unenforceable',
          example: 'Best practice: SVP/EVP level or higher; confirm with legal',
        },
      ],
    },
  ],
};

// Deal type data
const dealTypes = [
  {
    id: 'licensing',
    name: 'Licensing / License Agreement',
    description: 'Grant rights to develop/commercialize your asset in specific territories or indications',
    icon: FileText,
    color: 'blue',
    keyTerms: ['Territory rights', 'Field of use', 'Exclusivity', 'Sublicense rights'],
    pros: ['Retain ownership', 'Upfront + milestones + royalties', 'Partner handles development costs'],
    cons: ['Less control over development', 'Lower total value vs acquisition', 'Royalty caps common'],
    typical: 'Phase 1-2 assets, platform technologies',
    example: {
      deal: 'Vertex + Orna Therapeutics (Jan 2025)',
      value: '$4.08B total',
      structure: '$65M upfront + $635M milestones + $365M/product options'
    }
  },
  {
    id: 'codev',
    name: 'Co-Development / Collaboration',
    description: 'Joint development with shared costs, risks, and profits',
    icon: Users2,
    color: 'green',
    keyTerms: ['Cost sharing ratio', 'Profit split', 'Decision rights', 'Opt-out provisions'],
    pros: ['Shared risk', 'Retain more upside', 'Access partner expertise'],
    cons: ['Shared decision-making', 'Complex governance', 'Capital required'],
    typical: 'Platform deals, early-stage programs',
    example: {
      deal: 'Amgen + AstraZeneca Collaboration',
      value: 'Multiple programs',
      structure: '50/50 cost share, profit split by territory'
    }
  },
  {
    id: 'acquisition',
    name: 'Acquisition / M&A',
    description: 'Full company or asset sale with complete transfer of ownership',
    icon: Building2,
    color: 'purple',
    keyTerms: ['Purchase price', 'CVRs', 'Escrow', 'Earnouts'],
    pros: ['Immediate liquidity', 'Clean exit', 'Premium valuation possible'],
    cons: ['Lose all future upside', 'Integration risks', 'Employee retention'],
    typical: 'Late-stage assets, platform companies',
    example: {
      deal: 'AbbVie acquired Aliada Therapeutics (Oct 2024)',
      value: '$1.4B',
      structure: 'All cash, completed Dec 2024'
    }
  },
  {
    id: 'option',
    name: 'Option Agreement',
    description: 'Partner pays for the right (not obligation) to license/acquire later',
    icon: Clock,
    color: 'orange',
    keyTerms: ['Option fee', 'Option period', 'Exercise price', 'Data package'],
    pros: ['Non-dilutive funding', 'Maintain optionality', 'Validation signal'],
    cons: ['Partner may not exercise', 'Lower upfront', 'Exclusivity period'],
    typical: 'Early discovery, preclinical assets',
    example: {
      deal: 'Typical Option Structure',
      value: '$5-20M option fee',
      structure: '12-24 month period, pre-agreed license terms'
    }
  },
];

// Deal economics data
const economicsData = {
  upfront: {
    title: 'Upfront Payment',
    description: 'Cash paid at signing, non-refundable',
    icon: DollarSign,
    benchmarks: [
      { stage: 'Preclinical', range: '$5M - $50M', median: '$15M' },
      { stage: 'Phase 1', range: '$20M - $100M', median: '$50M' },
      { stage: 'Phase 2', range: '$50M - $300M', median: '$100M' },
      { stage: 'Phase 3', range: '$100M - $1B+', median: '$300M' },
    ],
    tips: [
      'Hot modalities (ADC, mRNA, gene therapy) command premiums',
      'Orphan/rare disease often higher upfront %',
      'Competitive process can increase 2-3x',
    ]
  },
  milestones: {
    title: 'Milestone Payments',
    description: 'Payments triggered by achieving specific goals',
    icon: TrendingUp,
    categories: [
      {
        name: 'Development Milestones',
        examples: ['IND filing', 'Phase 1 start', 'Phase 2 data', 'Phase 3 start', 'NDA/BLA filing'],
        typical: '30-50% of total deal value'
      },
      {
        name: 'Regulatory Milestones',
        examples: ['FDA approval', 'EMA approval', 'Japan approval', 'China approval'],
        typical: '15-25% of total deal value'
      },
      {
        name: 'Commercial Milestones',
        examples: ['First commercial sale', '$500M sales', '$1B sales', '$2B sales'],
        typical: '20-40% of total deal value'
      },
    ],
    tips: [
      'Development milestones most likely to be achieved',
      'Sales milestones often capped or tiered',
      'Negotiate milestone timing carefully',
    ]
  },
  royalties: {
    title: 'Royalties',
    description: 'Percentage of net sales paid to licensor',
    icon: TrendingUp,
    benchmarks: [
      { stage: 'Preclinical', range: '1-5%', typical: 'Single digits' },
      { stage: 'Phase 1', range: '3-8%', typical: 'Mid single digits' },
      { stage: 'Phase 2', range: '5-12%', typical: 'High single digits' },
      { stage: 'Phase 3+', range: '10-20%', typical: 'Low double digits' },
    ],
    tips: [
      'Tiered royalties common (higher % at higher sales)',
      'Watch for royalty reductions (generics, patent expiry)',
      'Net sales definition is critical - negotiate carefully',
    ]
  }
};

// Deal process steps
const dealProcess = [
  {
    step: 1,
    name: 'Initial Contact',
    duration: '1-4 weeks',
    description: 'Intro meetings, high-level pitch, mutual interest assessment',
    deliverables: ['Teaser deck', 'Company overview', 'Asset summary'],
  },
  {
    step: 2,
    name: 'NDA & Data Sharing',
    duration: '2-4 weeks',
    description: 'Sign confidentiality agreement, share detailed data package',
    deliverables: ['Mutual NDA', 'Data room access', 'Scientific presentations'],
  },
  {
    step: 3,
    name: 'Due Diligence',
    duration: '4-12 weeks',
    description: 'Deep dive into science, IP, CMC, regulatory, commercial',
    deliverables: ['DD Q&A responses', 'Site visits', 'Expert calls'],
  },
  {
    step: 4,
    name: 'Term Sheet',
    duration: '2-6 weeks',
    description: 'Non-binding outline of key deal terms',
    deliverables: ['Term sheet draft', 'Negotiations', 'Board approval'],
  },
  {
    step: 5,
    name: 'Definitive Agreement',
    duration: '6-12 weeks',
    description: 'Binding legal contract with all terms',
    deliverables: ['License agreement', 'Schedules & exhibits', 'Signing & closing'],
  },
];

// Preparation checklist
const preparationChecklist = [
  {
    category: 'Data Package',
    items: [
      { name: 'Efficacy data (in vitro, in vivo, clinical)', critical: true },
      { name: 'Safety/toxicology data', critical: true },
      { name: 'CMC/manufacturing documentation', critical: true },
      { name: 'Regulatory history & correspondence', critical: false },
      { name: 'Competitive landscape analysis', critical: false },
    ]
  },
  {
    category: 'IP & Legal',
    items: [
      { name: 'Patent portfolio summary', critical: true },
      { name: 'Freedom-to-operate analysis', critical: true },
      { name: 'Material transfer agreements', critical: false },
      { name: 'Existing encumbrances/licenses', critical: true },
    ]
  },
  {
    category: 'Corporate',
    items: [
      { name: 'Cap table & corporate structure', critical: true },
      { name: 'Board approval for deal process', critical: true },
      { name: 'Key employee retention plans', critical: false },
      { name: 'Financial projections', critical: false },
    ]
  },
  {
    category: 'Commercial',
    items: [
      { name: 'Market sizing & opportunity', critical: true },
      { name: 'Pricing & reimbursement analysis', critical: false },
      { name: 'Competitive positioning', critical: true },
      { name: 'Commercial strategy outline', critical: false },
    ]
  },
];

// Recent deal comps
const recentDeals = [
  {
    date: 'Jan 2025',
    seller: 'Orna Therapeutics',
    buyer: 'Vertex',
    asset: 'LNP gene editing platform',
    indication: 'Sickle cell, TDT',
    stage: 'Preclinical',
    upfront: '$65M',
    total: '$4.08B',
    modality: 'Gene Therapy',
  },
  {
    date: 'Dec 2024',
    seller: 'Sangamo',
    buyer: 'Astellas',
    asset: 'STAC-BBB AAV capsid',
    indication: 'Neurological diseases',
    stage: 'Preclinical',
    upfront: '$20M',
    total: '$1.32B',
    modality: 'Gene Therapy',
  },
  {
    date: 'Oct 2024',
    seller: 'Aliada Therapeutics',
    buyer: 'AbbVie',
    asset: 'MODEL platform + ALIA-1758',
    indication: "Alzheimer's",
    stage: 'Phase 1',
    upfront: '$1.4B',
    total: '$1.4B (acquisition)',
    modality: 'Antibody',
  },
  {
    date: 'Aug 2024',
    seller: 'Sangamo',
    buyer: 'Genentech',
    asset: 'STAC-BBB + zinc finger',
    indication: 'Neurodegeneration',
    stage: 'Preclinical',
    upfront: 'Undisclosed',
    total: '$1.95B',
    modality: 'Gene Therapy',
  },
];

// Contract Types Overview
const contractTypes = [
  {
    id: 'license',
    name: 'License Agreement',
    icon: '📄',
    color: 'blue',
    subtitle: 'Exclusive / Non-Exclusive License',
    description: 'Grant rights to develop and commercialize your asset in exchange for upfront, milestones, and royalties.',
    bestFor: ['Out-licensing assets', 'Retaining ownership', 'Earning royalties on sales'],
    keyTerms: ['License Grant', 'Territory', 'Field of Use', 'Royalties', 'Milestones'],
    typicalValue: '$10M-$500M+ upfront + milestones + royalties',
    exampleDeals: ['Vertex-Orna ($4B)', 'Amgen-Vigil', 'XOMA-Novartis'],
  },
  {
    id: 'collaboration',
    name: 'Collaboration Agreement',
    icon: '🤝',
    color: 'green',
    subtitle: 'Co-Development / Research Partnership',
    description: 'Joint development with shared costs, risks, and profits. Both parties contribute resources.',
    bestFor: ['Sharing development risk', 'Accessing partner expertise', 'Platform deals'],
    keyTerms: ['Cost Sharing', 'Profit Split', 'Joint Steering Committee', 'Opt-in/Opt-out Rights'],
    typicalValue: 'Lower upfront + 50/50 profit split',
    exampleDeals: ['Nurix-Sanofi', 'Amgen-AstraZeneca'],
  },
  {
    id: 'manda',
    name: 'M&A Agreement',
    icon: '🏢',
    color: 'purple',
    subtitle: 'Acquisition / Merger',
    description: 'Full company or asset sale with complete transfer of ownership. Clean exit for shareholders.',
    bestFor: ['Complete exit', 'Immediate liquidity', 'Late-stage assets'],
    keyTerms: ['Purchase Price', 'Representations & Warranties', 'Indemnification', 'Earnouts/CVRs'],
    typicalValue: '$500M-$10B+ at closing',
    exampleDeals: ['AbbVie-Aliada ($1.4B)', 'Amgen-Horizon ($28B)'],
  },
  {
    id: 'option',
    name: 'Option Agreement',
    icon: '⏰',
    color: 'orange',
    subtitle: 'Option to License / Acquire',
    description: 'Partner pays for the right (not obligation) to license or acquire at a later date after seeing more data.',
    bestFor: ['Early-stage assets', 'Non-dilutive funding', 'Maintaining optionality'],
    keyTerms: ['Option Fee', 'Option Period', 'Exercise Terms', 'Pre-negotiated License Terms'],
    typicalValue: '$5M-$50M option fee + pre-agreed license terms',
    exampleDeals: ['Typical for preclinical/discovery stage'],
  },
];

// Mock Contract Templates for each type
const contractTemplates = {
  license: {
    title: 'EXCLUSIVE LICENSE AGREEMENT',
  parties: {
    licensor: '[LICENSOR]',
    licensee: '[LICENSEE]',
  },
  sections: [
    {
      id: 'recitals',
      title: 'RECITALS',
      content: `WHEREAS, Licensor possesses certain proprietary technology, patents, and know-how relating to [COMPOUND/TECHNOLOGY] (the "Licensed Technology");

WHEREAS, Licensee desires to obtain an exclusive license to develop and commercialize products based on the Licensed Technology;

NOW, THEREFORE, in consideration of the mutual covenants and agreements set forth herein, the Parties agree as follows:`,
      annotations: [],
    },
    {
      id: 'article1',
      title: 'ARTICLE 1. DEFINITIONS',
      content: `1.1 "Affiliate" means any entity that directly or indirectly controls, is controlled by, or is under common control with a Party, where "control" means ownership of fifty percent (50%) or more of the voting securities.

1.2 "Field" means [THERAPEUTIC AREA/INDICATION].

1.3 "Licensed Patents" means the patents and patent applications listed in Exhibit A.

1.4 "Licensed Product" means any pharmaceutical product containing a Licensed Compound.

1.5 "Net Sales" means the gross amounts invoiced for sales of Licensed Products to Third Parties, less: (a) trade discounts and rebates; (b) credits for returns; (c) freight and insurance; (d) sales taxes; and (e) customs duties.

1.6 "Territory" means [WORLDWIDE / SPECIFIC REGIONS].`,
      annotations: [
        {
          id: 'field-def',
          term: '"Field"',
          type: 'critical',
          title: 'Field of Use Definition',
          importance: 'Critical - Defines scope of licensed rights',
          tips: [
            'Be specific about indications vs. broad therapeutic areas',
            'Consider retaining rights for certain indications',
            'Negotiate expansion rights for new indications',
          ],
          redFlag: 'Overly broad field limits future partnering options',
          example: 'Good: "oncology indications excluding hematological malignancies" vs Bad: "all human diseases"',
        },
        {
          id: 'net-sales-def',
          term: '"Net Sales"',
          type: 'warning',
          title: 'Net Sales Definition',
          importance: 'High - Directly impacts royalty calculations',
          tips: [
            'Review each deduction category carefully',
            'Cap certain deductions (e.g., max 5% for distribution fees)',
            'Exclude affiliate transfers at non-arms-length prices',
          ],
          redFlag: 'Excessive deductions can reduce royalties by 30-40%',
          example: 'Watch for: "promotional discounts", "free goods programs", "patient assistance"',
        },
        {
          id: 'territory-def',
          term: '"Territory"',
          type: 'critical',
          title: 'Territory Definition',
          importance: 'Critical - Geographic scope of rights',
          tips: [
            'Consider retaining rights in home market',
            'Negotiate expansion options for excluded territories',
            'Split by development vs. commercialization rights',
          ],
          redFlag: 'Worldwide grants leave no room for regional deals',
          example: 'Structured: "North America and EU for commercialization; worldwide for development"',
        },
      ],
    },
    {
      id: 'article2',
      title: 'ARTICLE 2. LICENSE GRANT',
      content: `2.1 License Grant. Subject to the terms of this Agreement, Licensor hereby grants to Licensee an exclusive, royalty-bearing license, with the right to sublicense (subject to Section 2.2), under the Licensed Patents and Licensed Know-How, to research, develop, manufacture, have manufactured, use, import, offer for sale, sell, and otherwise commercialize Licensed Products in the Field in the Territory.

2.2 Sublicensing. Licensee may grant sublicenses under the rights granted in Section 2.1, provided that: (a) each sublicense shall be consistent with the terms of this Agreement; (b) Licensee shall remain responsible for the performance of its sublicensees; and (c) Licensee shall provide Licensor with a copy of each sublicense within [30] days of execution.

2.3 Retained Rights. Licensor retains the right to: (a) practice the Licensed Technology for internal research purposes; (b) grant licenses outside the Field; and (c) publish scientific findings subject to Section 8.`,
      annotations: [
        {
          id: 'license-grant',
          term: 'exclusive, royalty-bearing license',
          type: 'critical',
          title: 'License Grant Scope',
          importance: 'Critical - Core of the deal',
          tips: [
            'Clarify if "exclusive" excludes Licensor too',
            'Specify all rights needed (make, use, sell, import)',
            'Consider co-exclusive for certain uses',
          ],
          redFlag: 'Ambiguous scope leads to disputes',
          example: '"exclusive (even as to Licensor)" is clearer than just "exclusive"',
        },
        {
          id: 'sublicense',
          term: 'right to sublicense',
          type: 'warning',
          title: 'Sublicensing Rights',
          importance: 'High - Affects deal flexibility',
          tips: [
            'Require consent for sublicenses (not unreasonably withheld)',
            'Ensure sublicensee obligations flow down',
            'Negotiate sublicense fee sharing',
          ],
          redFlag: 'Unrestricted sublicensing can put your IP in unknown hands',
          example: 'Require: approval for sublicenses, copy of agreements, direct recourse rights',
        },
        {
          id: 'retained-rights',
          term: 'Retained Rights',
          type: 'info',
          title: 'Licensor Retained Rights',
          importance: 'Medium - Protects your interests',
          tips: [
            'Always retain research/academic use rights',
            'Consider manufacturing rights for supply security',
            'Retain rights outside licensed field',
          ],
          redFlag: 'Giving up all rights limits future options',
          example: 'Retain: internal R&D, academic collaborations, manufacturing know-how',
        },
      ],
    },
    {
      id: 'article3',
      title: 'ARTICLE 3. FINANCIAL TERMS',
      content: `3.1 Upfront Payment. Within [10] Business Days of the Effective Date, Licensee shall pay to Licensor a non-refundable, non-creditable upfront payment of [AMOUNT] Dollars (US$[X]).

3.2 Development Milestones. Licensee shall pay to Licensor the following non-refundable milestone payments upon first achievement of each event:
    (a) IND Filing (first indication): $[X]M
    (b) Initiation of Phase 2 Clinical Trial: $[X]M
    (c) Initiation of Phase 3 Clinical Trial: $[X]M
    (d) FDA Approval (first indication): $[X]M
    (e) EMA Approval: $[X]M

3.3 Commercial Milestones. Licensee shall pay to Licensor the following payments upon first achievement:
    (a) First Commercial Sale: $[X]M
    (b) Annual Net Sales exceeding $500M: $[X]M
    (c) Annual Net Sales exceeding $1B: $[X]M

3.4 Royalties. Licensee shall pay to Licensor royalties on Net Sales of Licensed Products at the following rates:
    (a) [X]% on Net Sales up to $[Y]B annually
    (b) [X+2]% on Net Sales between $[Y]B and $[Z]B
    (c) [X+4]% on Net Sales exceeding $[Z]B

3.5 Royalty Reductions. Royalties may be reduced by [X]% in the event of: (a) expiration of the last Valid Claim covering such Licensed Product in such country; or (b) entry of a Generic Product capturing more than [X]% market share.`,
      annotations: [
        {
          id: 'upfront',
          term: 'upfront payment',
          type: 'info',
          title: 'Upfront Payment',
          importance: 'Guaranteed cash at signing',
          tips: [
            'Negotiate payment timing carefully',
            'Ensure "non-creditable" against royalties',
            'Consider tax implications of structure',
          ],
          benchmark: 'Preclinical: $5-50M | Phase 1: $20-100M | Phase 2: $50-300M',
          example: 'Best practice: payment within 5-10 business days of Effective Date',
        },
        {
          id: 'milestones',
          term: 'milestone payments',
          type: 'warning',
          title: 'Milestone Payments',
          importance: 'High - Tied to development progress',
          tips: [
            'Use objective, verifiable triggers',
            'Specify payment timing (within X days)',
            'Consider per-indication vs. one-time payments',
          ],
          redFlag: 'Vague milestones like "successful completion" lead to disputes',
          example: 'Good: "first dosing of first patient in Phase 2" vs Bad: "Phase 2 initiation"',
        },
        {
          id: 'royalties',
          term: 'Royalties',
          type: 'critical',
          title: 'Royalty Rates',
          importance: 'Critical - Long-term revenue stream',
          tips: [
            'Tiered rates reward success',
            'Set floor rates (minimum royalty)',
            'Limit reduction triggers',
          ],
          benchmark: 'Preclinical: 1-5% | Phase 1: 3-8% | Phase 2: 5-12% | Phase 3: 10-20%',
          example: 'Negotiate: caps on reductions, combination product allocation formula',
        },
        {
          id: 'royalty-reduction',
          term: 'Royalty Reductions',
          type: 'danger',
          title: 'Royalty Reduction Provisions',
          importance: 'High Risk - Can significantly reduce payments',
          tips: [
            'Cap total reductions (e.g., max 50%)',
            'Set royalty floors',
            'Limit generic impact triggers',
          ],
          redFlag: 'Unlimited reductions can reduce royalties to near zero',
          example: 'Protect with: "notwithstanding the foregoing, royalties shall not be reduced below [X]%"',
        },
      ],
    },
    {
      id: 'article4',
      title: 'ARTICLE 4. DEVELOPMENT AND COMMERCIALIZATION',
      content: `4.1 Diligence. Licensee shall use Commercially Reasonable Efforts to develop and commercialize at least one Licensed Product in the Field in the Territory.

4.2 Development Plan. Within [90] days of the Effective Date, Licensee shall provide Licensor with a development plan setting forth the anticipated development activities and timelines.

4.3 Reports. Licensee shall provide Licensor with written progress reports on a [quarterly/semi-annual] basis.

4.4 Regulatory. Licensee shall be responsible for all regulatory filings and shall own all Regulatory Approvals for Licensed Products.`,
      annotations: [
        {
          id: 'diligence',
          term: 'Commercially Reasonable Efforts',
          type: 'critical',
          title: 'Diligence Standard',
          importance: 'Critical - Ensures partner actually develops product',
          tips: [
            'Define "Commercially Reasonable Efforts" explicitly',
            'Include objective milestones with timelines',
            'Add reversion rights for failure',
          ],
          redFlag: '"Best efforts" is too vague and hard to enforce',
          example: 'Define as: "efforts consistent with those used by similarly situated companies for products of similar potential"',
        },
        {
          id: 'dev-plan',
          term: 'development plan',
          type: 'info',
          title: 'Development Plan',
          importance: 'Medium - Provides visibility',
          tips: [
            'Require regular updates',
            'Include key milestones and timelines',
            'Negotiate approval rights for major changes',
          ],
          redFlag: 'No plan means no accountability',
          example: 'Require: annual updates, JSC review, notification of material changes',
        },
      ],
    },
    {
      id: 'article5',
      title: 'ARTICLE 5. INTELLECTUAL PROPERTY',
      content: `5.1 Ownership. As between the Parties: (a) Licensor shall own all Licensed Technology; (b) Licensee shall own all inventions made solely by Licensee; (c) Joint Inventions shall be jointly owned.

5.2 Prosecution. Licensor shall be responsible for prosecution and maintenance of Licensed Patents, at Licensee's cost. Licensor shall consult with Licensee on material prosecution decisions.

5.3 Enforcement. Licensee shall have the first right to enforce Licensed Patents against infringers in the Field in the Territory. If Licensee does not initiate action within [90] days of notice, Licensor may do so.

5.4 Improvements. Each Party shall promptly disclose to the other any Improvements to the Licensed Technology. Licensor shall have an exclusive license to Licensee Improvements outside the Field.`,
      annotations: [
        {
          id: 'ip-ownership',
          term: 'Joint Inventions shall be jointly owned',
          type: 'warning',
          title: 'IP Ownership',
          importance: 'High - Affects future rights',
          tips: [
            'Define inventorship criteria clearly',
            'Specify rights to exploit joint IP',
            'Address prosecution cost sharing',
          ],
          redFlag: 'Licensee-owns-all clauses strip your IP rights',
          example: 'Fair: "sole inventions owned by inventing party; joint inventions jointly owned"',
        },
        {
          id: 'prosecution',
          term: 'prosecution and maintenance',
          type: 'warning',
          title: 'Patent Prosecution',
          importance: 'High - Protects IP value',
          tips: [
            'Retain control or strong consultation rights',
            'Require advance notice of abandonment',
            'Negotiate step-in rights',
          ],
          redFlag: 'Losing prosecution control can let partner abandon key patents',
          example: 'Require: 60-day notice before abandonment, right to assume prosecution',
        },
        {
          id: 'improvements',
          term: 'Improvements',
          type: 'danger',
          title: 'Improvement Clauses',
          importance: 'High Risk - Can expand partner rights',
          tips: [
            'Limit scope of improvement grant-backs',
            'Retain ownership of your improvements',
            'Time-limit any grant-back obligations',
          ],
          redFlag: 'Broad improvement clauses give away future innovations',
          example: 'Limit to: "improvements necessary to practice the license" not "all improvements"',
        },
      ],
    },
    {
      id: 'article6',
      title: 'ARTICLE 6. TERM AND TERMINATION',
      content: `6.1 Term. This Agreement shall commence on the Effective Date and continue until the expiration of all royalty obligations, unless earlier terminated.

6.2 Termination for Breach. Either Party may terminate this Agreement upon [60] days written notice if the other Party materially breaches this Agreement and fails to cure within such period.

6.3 Termination for Convenience. Licensee may terminate this Agreement for convenience upon [180] days prior written notice.

6.4 Effects of Termination. Upon termination: (a) all licenses shall revert to Licensor; (b) Licensee shall transfer all regulatory filings and data; (c) Licensee shall grant Licensor a license to any Licensee Improvements necessary to exploit Licensed Products.

6.5 Survival. Sections [X], [Y], and [Z] shall survive termination.`,
      annotations: [
        {
          id: 'term-convenience',
          term: 'Termination for Convenience',
          type: 'danger',
          title: 'Convenience Termination',
          importance: 'High Risk - Partner can walk away',
          tips: [
            'Require longer notice (180+ days)',
            'Add termination fees',
            'Ensure wind-down provisions for trials',
          ],
          redFlag: 'Short notice periods can strand your asset mid-development',
          example: 'Protect with: "180 days notice plus payment of [X]% remaining milestones"',
        },
        {
          id: 'effects',
          term: 'Effects of Termination',
          type: 'critical',
          title: 'Termination Effects',
          importance: 'Critical - Determines what you get back',
          tips: [
            'Ensure full rights reversion',
            'Require data/regulatory file transfer',
            'Negotiate transition assistance',
          ],
          redFlag: 'Unclear reversion leaves you without rights to continue',
          example: 'Require: "all licenses revert; all data transferred; transition services for [X] months"',
        },
      ],
    },
    {
      id: 'article7',
      title: 'ARTICLE 7. INDEMNIFICATION',
      content: `7.1 By Licensee. Licensee shall indemnify and hold harmless Licensor from claims arising from: (a) Licensee's development or commercialization of Licensed Products; (b) Licensee's breach of this Agreement; (c) Licensee's negligence or willful misconduct.

7.2 By Licensor. Licensor shall indemnify and hold harmless Licensee from claims arising from: (a) breach of Licensor's representations; (b) Licensor's negligence or willful misconduct; (c) claims that Licensed Technology infringes Third Party IP rights existing as of the Effective Date.

7.3 Procedure. The indemnified Party shall provide prompt notice and reasonable cooperation. The indemnifying Party shall control the defense.

7.4 Limitation. NEITHER PARTY SHALL BE LIABLE FOR INDIRECT, INCIDENTAL, OR CONSEQUENTIAL DAMAGES.`,
      annotations: [
        {
          id: 'indem-mutual',
          term: 'indemnify and hold harmless',
          type: 'warning',
          title: 'Indemnification Structure',
          importance: 'High - Allocates liability risk',
          tips: [
            'Ensure mutual/balanced indemnification',
            'Require negligence/willful misconduct qualifiers',
            'Include IP infringement coverage',
          ],
          redFlag: 'One-sided indemnification exposes you unfairly',
          example: 'Balanced: each party indemnifies for own conduct, breach, and negligence',
        },
        {
          id: 'limitation',
          term: 'LIMITATION',
          type: 'info',
          title: 'Liability Caps',
          importance: 'Medium - Limits exposure',
          tips: [
            'Carve out fraud, willful misconduct, IP indemnity',
            'Consider insurance requirements',
            'Set reasonable caps if needed',
          ],
          redFlag: 'Unlimited liability can exceed company value',
          example: 'Standard: exclude indirect damages; cap at [X]x fees paid or insurance limits',
        },
      ],
    },
  ],
  },
  // Collaboration Agreement Template
  collaboration: {
    title: 'COLLABORATION AND LICENSE AGREEMENT',
    parties: {
      licensor: '[COMPANY A]',
      licensee: '[COMPANY B]',
    },
    sections: [
      {
        id: 'recitals',
        title: 'RECITALS',
        content: `WHEREAS, Company A possesses proprietary technology and expertise in [TECHNOLOGY PLATFORM];

WHEREAS, Company B possesses expertise in clinical development and commercialization of pharmaceutical products;

WHEREAS, the Parties desire to collaborate on the research, development, and commercialization of products based on the Technology Platform;

NOW, THEREFORE, the Parties agree as follows:`,
        annotations: [],
      },
      {
        id: 'article1',
        title: 'ARTICLE 1. DEFINITIONS',
        content: `1.1 "Collaboration Target" means the molecular targets selected by the JSC for research under this Agreement.

1.2 "Development Costs" means all costs incurred in the development of Collaboration Products.

1.3 "Joint Steering Committee" or "JSC" means the committee established under Article 5 to oversee the Collaboration.

1.4 "Net Profits" means Net Sales less Cost of Goods Sold and Allowable Expenses.

1.5 "Opt-In Right" means the right of Company B to elect to co-develop and co-commercialize a Collaboration Product.

1.6 "Profit Share Territory" means the United States.

1.7 "Royalty Territory" means all countries outside the Profit Share Territory.`,
        annotations: [
          {
            id: 'jsc-def',
            term: '"Joint Steering Committee"',
            type: 'critical',
            title: 'Governance Structure',
            importance: 'Critical - Controls decision-making',
            tips: [
              'Define voting rights and tie-breaker mechanisms',
              'Specify which decisions require unanimous consent',
              'Include escalation procedures for disputes',
            ],
            redFlag: 'Partner having final decision rights on key matters',
            example: 'Balance: "Each Party has one vote; deadlocks escalated to executives within 30 days"',
          },
          {
            id: 'opt-in',
            term: '"Opt-In Right"',
            type: 'warning',
            title: 'Opt-In/Opt-Out Rights',
            importance: 'High - Determines partner commitment',
            tips: [
              'Define clear opt-in timing and data requirements',
              'Specify cost-sharing changes upon opt-in',
              'Include consequences of opt-out',
            ],
            redFlag: 'Partner can opt-in late after you bear most risk',
            example: 'Fair: "Opt-in within 60 days of Phase 2 data; back-pay 50% of prior costs"',
          },
          {
            id: 'profit-split',
            term: '"Net Profits"',
            type: 'critical',
            title: 'Profit Split Mechanics',
            importance: 'Critical - Determines your economics',
            tips: [
              'Define "Allowable Expenses" carefully',
              'Cap overhead allocations',
              'Audit rights on cost reporting',
            ],
            redFlag: 'Uncapped expenses can eat into profits',
            example: 'Protect: "Overhead capped at 10% of direct costs; quarterly reconciliation"',
          },
        ],
      },
      {
        id: 'article2',
        title: 'ARTICLE 2. RESEARCH COLLABORATION',
        content: `2.1 Research Program. The Parties shall conduct a joint research program to identify and validate Collaboration Targets.

2.2 Research Plan. The JSC shall approve a Research Plan within [60] days of the Effective Date.

2.3 Research Costs. Each Party shall bear its own internal costs. External costs shall be shared equally.

2.4 Target Selection. Each Party may propose up to [X] targets per year for inclusion in the Collaboration.

2.5 Exclusivity. During the Research Term, neither Party shall conduct research on Collaboration Targets outside this Agreement.`,
        annotations: [
          {
            id: 'exclusivity-collab',
            term: 'Exclusivity',
            type: 'warning',
            title: 'Exclusivity Obligations',
            importance: 'High - Limits your freedom',
            tips: [
              'Limit exclusivity scope to specific targets only',
              'Include time limits on exclusivity',
              'Negotiate carve-outs for ongoing programs',
            ],
            redFlag: 'Broad exclusivity on therapeutic area, not just targets',
            example: 'Limit to: "Collaboration Targets only, not entire therapeutic area"',
          },
        ],
      },
      {
        id: 'article3',
        title: 'ARTICLE 3. DEVELOPMENT AND COMMERCIALIZATION',
        content: `3.1 Opt-In Election. Company B may elect to opt-in to co-develop any Collaboration Product by providing notice within [60] days of receiving the Phase 1 Data Package.

3.2 Cost Sharing. Following opt-in, Development Costs shall be shared [50/50] in the Profit Share Territory.

3.3 Commercialization. In the Profit Share Territory, Net Profits shall be shared [50/50]. In the Royalty Territory, Company B shall pay royalties of [X-Y]% on Net Sales.

3.4 Lead Party. Company B shall be the Lead Party for all development and commercialization activities, subject to JSC oversight.

3.5 Diligence. The Lead Party shall use Commercially Reasonable Efforts to develop and commercialize at least one Collaboration Product.`,
        annotations: [
          {
            id: 'cost-share',
            term: 'Cost Sharing',
            type: 'critical',
            title: 'Development Cost Sharing',
            importance: 'Critical - Major financial commitment',
            tips: [
              'Define what costs are included/excluded',
              'Set annual budget caps or approval thresholds',
              'Reconcile costs quarterly',
            ],
            redFlag: 'Unlimited cost obligations can drain your cash',
            example: 'Protect: "Annual budget approved by JSC; overruns require consent; quarterly true-up"',
          },
          {
            id: 'profit-share',
            term: 'Net Profits shall be shared',
            type: 'critical',
            title: 'Profit Sharing',
            importance: 'Critical - Your upside',
            tips: [
              'Define Net Profits formula precisely',
              'Cap allocable overhead costs',
              'Include manufacturing margin',
            ],
            benchmark: 'Typical: 50/50 in US, royalties ex-US',
            example: 'Watch: COGS definition, sales force costs, commercial overhead',
          },
        ],
      },
      {
        id: 'article4',
        title: 'ARTICLE 4. GOVERNANCE',
        content: `4.1 Joint Steering Committee. The Parties shall establish a JSC consisting of [3] representatives from each Party.

4.2 Decisions. The JSC shall make decisions by unanimous consent. Each Party shall have one vote.

4.3 Deadlock. If the JSC cannot reach agreement within [30] days, the matter shall be escalated to the Executive Officers.

4.4 Final Decision Rights. If the Executive Officers cannot resolve a deadlock within [15] days:
    (a) Company A shall have final decision authority on research matters
    (b) Company B shall have final decision authority on development and commercialization matters
    (c) Decisions on safety matters shall require mutual consent

4.5 Subcommittees. The JSC may establish subcommittees (JDC, JCC) for specific functions.`,
        annotations: [
          {
            id: 'final-decision',
            term: 'Final Decision Rights',
            type: 'danger',
            title: 'Tie-Breaker Rights',
            importance: 'High Risk - Can override your input',
            tips: [
              'Limit partner final decision rights to their expertise areas',
              'Protect your core interests (IP, safety) with consent rights',
              'Include opt-out rights if you disagree with key decisions',
            ],
            redFlag: 'Partner controls decisions that affect your IP or platform',
            example: 'Protect: research & IP matters require your consent; partner leads commercialization',
          },
        ],
      },
      {
        id: 'article5',
        title: 'ARTICLE 5. MANUFACTURING',
        content: `5.1 Manufacturing Responsibility. Company B shall be responsible for manufacturing of all Collaboration Products for clinical and commercial supply.

5.2 Technology Transfer. Upon request, Company A shall transfer all manufacturing Know-How to Company B or its designated CMO.

5.3 Quality Agreement. The Parties shall enter into a Quality Agreement prior to manufacture of clinical supplies.

5.4 Supply Pricing. Supply of Collaboration Products shall be at [Cost Plus X%] for clinical supplies and [Cost Plus Y%] for commercial supplies.

5.5 Second Source. Company B shall have the right to qualify a second source manufacturer.`,
        annotations: [
          {
            id: 'tech-transfer',
            term: 'Technology Transfer',
            type: 'warning',
            title: 'Manufacturing Tech Transfer',
            importance: 'High - Protects your know-how',
            tips: [
              'Limit transfer to what is necessary for manufacturing only',
              'Retain right to manufacture for your own territory/uses',
              'Include confidentiality and non-use provisions',
            ],
            redFlag: 'Unlimited tech transfer gives away your process IP',
            example: 'Limit: "Transfer only what is necessary; retain right to manufacture for opt-out products"',
          },
        ],
      },
      {
        id: 'article6',
        title: 'ARTICLE 6. REGULATORY',
        content: `6.1 Regulatory Responsibility. Company B shall be responsible for all regulatory filings and interactions with Regulatory Authorities.

6.2 Regulatory Strategy. The JSC shall approve the regulatory strategy for each Collaboration Product.

6.3 Right of Reference. Each Party grants the other Party a Right of Reference to all regulatory filings.

6.4 Safety Reporting. Each Party shall promptly report all safety information to the other Party. Company B shall be responsible for pharmacovigilance.

6.5 Recalls. Company B shall have decision authority on recalls, but shall consult with Company A.`,
        annotations: [
          {
            id: 'regulatory-strategy',
            term: 'regulatory strategy',
            type: 'warning',
            title: 'Regulatory Decision Rights',
            importance: 'High - Impacts approval timeline',
            tips: [
              'Ensure JSC approval for major regulatory decisions',
              'Include consultation rights on labeling',
              'Retain input on indication prioritization',
            ],
            redFlag: 'No input on regulatory strategy affecting your platform',
            example: 'Protect: "Regulatory strategy requires JSC approval; labeling requires mutual consent"',
          },
        ],
      },
      {
        id: 'article7',
        title: 'ARTICLE 7. FINANCIAL TERMS',
        content: `7.1 Upfront Payment. Company B shall pay Company A $[X] million within [10] days of the Effective Date.

7.2 Research Funding. Company B shall pay Company A $[Y] million per year for research activities.

7.3 Development Milestones.
    (a) IND Filing: $[X] million
    (b) Phase 1 Completion: $[X] million
    (c) Phase 2 Completion: $[X] million
    (d) Phase 3 Initiation: $[X] million
    (e) NDA Filing: $[X] million
    (f) FDA Approval: $[X] million

7.4 Sales Milestones. Upon achievement of annual Net Sales:
    (a) $[100] million: $[X] million
    (b) $[500] million: $[X] million
    (c) $[1] billion: $[X] million

7.5 Royalties. Company B shall pay royalties of [X-Y]% on Net Sales in the Royalty Territory.

7.6 Profit Share. In the Profit Share Territory, Net Profits shall be shared [50/50].`,
        annotations: [
          {
            id: 'milestones-collab',
            term: 'Development Milestones',
            type: 'critical',
            title: 'Milestone Structure',
            importance: 'Critical - Major value driver',
            tips: [
              'Front-load milestones for earlier cash',
              'Include indication expansion milestones',
              'Tie to objective, verifiable events',
            ],
            benchmark: 'Phase 3: $50-150M; Approval: $100-300M',
            example: 'Structure: 40% preclinical/Ph1, 30% Ph2/Ph3, 30% regulatory/commercial',
          },
          {
            id: 'royalties-collab',
            term: 'Royalties',
            type: 'critical',
            title: 'Royalty Rates',
            importance: 'Critical - Long-term value',
            tips: [
              'Tiered royalties based on sales levels',
              'Include anti-stacking provisions',
              'Protect floor royalty rate',
            ],
            benchmark: 'Typical: 8-15% tiered; higher for validated targets',
            example: 'Tiered: 10% up to $500M, 12% $500M-$1B, 15% above $1B',
          },
        ],
      },
      {
        id: 'article8',
        title: 'ARTICLE 8. INTELLECTUAL PROPERTY',
        content: `8.1 Background IP. Each Party retains ownership of its Background IP.

8.2 Collaboration IP. Inventions made solely by one Party shall be owned by that Party. Joint Inventions shall be jointly owned.

8.3 Patent Prosecution. The Party owning a Patent shall be responsible for prosecution. For Joint Patents, Company A shall lead prosecution with Company B consultation.

8.4 Patent Costs. Patent costs shall be shared equally for Joint Patents. Each Party bears costs for solely-owned Patents.

8.5 Enforcement. The Party with exclusive rights in a country shall lead enforcement. Recovery shall first reimburse enforcement costs, then be shared.

8.6 Platform IP. For clarity, all improvements to Company A's Platform shall be solely owned by Company A.`,
        annotations: [
          {
            id: 'platform-ip',
            term: 'Platform IP',
            type: 'danger',
            title: 'Platform Protection',
            importance: 'Critical - Protects your core asset',
            tips: [
              'Carve out platform improvements from joint IP',
              'Retain freedom to use platform for other programs',
              'Limit partner rights to specific products only',
            ],
            redFlag: 'Partner claims rights to platform improvements',
            example: 'Protect: "All Platform IP solely owned by Company A; license limited to Collaboration Products"',
          },
          {
            id: 'joint-ip',
            term: 'Joint Inventions',
            type: 'warning',
            title: 'Joint Ownership',
            importance: 'High - Complex to manage',
            tips: [
              'Define clear allocation criteria upfront',
              'Specify prosecution and enforcement rights',
              'Address freedom to license to Third Parties',
            ],
            redFlag: 'Ambiguous joint IP terms create future disputes',
            example: 'Clarify: "Joint Patents require mutual consent for Third Party licenses"',
          },
        ],
      },
      {
        id: 'article9',
        title: 'ARTICLE 9. TERM AND TERMINATION',
        content: `9.1 Term. This Agreement shall continue until the later of (a) expiration of all royalty obligations or (b) termination.

9.2 Termination for Convenience. Company B may terminate this Agreement upon [180] days written notice.

9.3 Termination for Cause. Either Party may terminate for material breach if not cured within [60] days.

9.4 Termination for Safety. Either Party may terminate if a serious safety issue arises that cannot be resolved.

9.5 Effects of Termination.
    (a) All licenses granted to Company B shall terminate
    (b) Company A may elect to continue development of terminated products
    (c) Company B shall grant Company A a license to Company B's improvements
    (d) Company B shall transfer all regulatory filings and data to Company A

9.6 Accrued Obligations. Termination shall not affect accrued payment obligations.`,
        annotations: [
          {
            id: 'termination-convenience',
            term: 'Termination for Convenience',
            type: 'warning',
            title: 'Convenience Termination',
            importance: 'High - Partner can walk away',
            tips: [
              'Require adequate notice period (180+ days)',
              'Include wind-down obligations',
              'Ensure data and material return',
            ],
            redFlag: 'Short notice period leaves you stranded',
            example: 'Require: 180 days notice; continue ongoing trials; transfer all data',
          },
          {
            id: 'reversion-rights',
            term: 'Company A may elect to continue',
            type: 'critical',
            title: 'Reversion Rights',
            importance: 'Critical - Get your asset back',
            tips: [
              'Automatic reversion of all rights',
              'License-back of partner improvements',
              'Transfer of regulatory filings',
              'Access to clinical data',
            ],
            redFlag: 'Weak reversion leaves you without ability to continue',
            example: 'Full reversion: rights, data, filings, partner improvements licensed royalty-free',
          },
        ],
      },
    ],
  },
  // M&A Agreement Template
  manda: {
    title: 'AGREEMENT AND PLAN OF MERGER',
    parties: {
      licensor: '[TARGET COMPANY]',
      licensee: '[ACQUIRER]',
    },
    sections: [
      {
        id: 'recitals',
        title: 'RECITALS',
        content: `WHEREAS, Acquirer desires to acquire Target through the merger of Merger Sub with and into Target;

WHEREAS, the Board of Directors of Target has determined that the Merger is in the best interests of Target and its shareholders;

WHEREAS, the Parties desire to set forth the terms and conditions of the Merger;

NOW, THEREFORE, the Parties agree as follows:`,
        annotations: [],
      },
      {
        id: 'article1',
        title: 'ARTICLE 1. THE MERGER',
        content: `1.1 The Merger. Subject to the terms and conditions of this Agreement, Merger Sub shall merge with and into Target, with Target surviving as a wholly-owned subsidiary of Acquirer.

1.2 Closing. The Closing shall take place on the date that is [3] Business Days after satisfaction of all Closing Conditions.

1.3 Merger Consideration. Each share of Target Common Stock shall be converted into the right to receive $[X] per share in cash (the "Merger Consideration").

1.4 Earnout. In addition to the Merger Consideration, shareholders shall be entitled to receive:
    (a) $[X] per share upon FDA approval of [PRODUCT]
    (b) $[X] per share upon achievement of $[Y] in Net Sales

1.5 Escrow. [X]% of the Merger Consideration shall be placed in escrow for [18] months to secure indemnification obligations.`,
        annotations: [
          {
            id: 'merger-consideration',
            term: 'Merger Consideration',
            type: 'critical',
            title: 'Purchase Price',
            importance: 'Critical - Total deal value',
            tips: [
              'Negotiate all-cash vs. stock consideration',
              'Consider tax implications',
              'Protect against price adjustments',
            ],
            redFlag: 'Stock consideration with no collar protection',
            example: 'Prefer: cash consideration; if stock, require 10% collar protection',
          },
          {
            id: 'earnout',
            term: 'Earnout',
            type: 'warning',
            title: 'Earnout/CVR Provisions',
            importance: 'High - Contingent value at risk',
            tips: [
              'Use objective, verifiable milestones',
              'Include development diligence obligations',
              'Protect against buyer sabotage',
            ],
            redFlag: 'Buyer can deprioritize product and avoid earnout',
            example: 'Protect: "Commercially Reasonable Efforts" + acceleration triggers',
          },
          {
            id: 'escrow',
            term: 'Escrow',
            type: 'warning',
            title: 'Escrow Holdback',
            importance: 'High - Delays your cash',
            tips: [
              'Minimize escrow percentage (target 5-10%)',
              'Shorten escrow period (12-18 months)',
              'Negotiate early release provisions',
            ],
            redFlag: 'Large escrow with broad indemnity claims',
            example: 'Standard: 10% for 18 months; 50% released at 12 months if no claims',
          },
        ],
      },
      {
        id: 'article2',
        title: 'ARTICLE 2. REPRESENTATIONS AND WARRANTIES',
        content: `2.1 Organization. Target is duly organized and validly existing under the laws of Delaware.

2.2 Capitalization. The authorized capital stock of Target consists of [X] shares, of which [Y] are issued and outstanding.

2.3 Intellectual Property. Target owns or has valid licenses to all Intellectual Property necessary for its business. To Target's Knowledge, the conduct of Target's business does not infringe any Third Party IP.

2.4 Material Contracts. Target has made available true and complete copies of all Material Contracts. Target is not in breach of any Material Contract.

2.5 Litigation. There is no pending or, to Target's Knowledge, threatened litigation against Target.

2.6 Regulatory Compliance. Target is in compliance with all applicable Laws and has all necessary Permits.`,
        annotations: [
          {
            id: 'ip-reps',
            term: 'Intellectual Property',
            type: 'critical',
            title: 'IP Representations',
            importance: 'Critical - High liability exposure',
            tips: [
              'Limit to actual knowledge where possible',
              'Qualify with disclosed matters',
              'Cap indemnification for IP claims',
            ],
            redFlag: 'Absolute representations on no infringement',
            example: 'Qualify: "To Target\'s Knowledge" + cap IP indemnity at [X]% of purchase price',
          },
          {
            id: 'knowledge',
            term: "Target's Knowledge",
            type: 'info',
            title: 'Knowledge Qualifiers',
            importance: 'Medium - Limits your liability',
            tips: [
              'Define "Knowledge" as actual knowledge of specified individuals',
              'Limit to executives, not all employees',
              'Exclude constructive knowledge',
            ],
            redFlag: '"Knowledge" includes all employees or constructive knowledge',
            example: 'Standard: "actual knowledge of CEO, CFO, CSO after reasonable inquiry"',
          },
        ],
      },
      {
        id: 'article3',
        title: 'ARTICLE 3. COVENANTS',
        content: `3.1 Conduct of Business. Between signing and closing, Target shall conduct its business in the ordinary course.

3.2 No-Shop. Target shall not solicit, initiate, or encourage any Acquisition Proposal from Third Parties.

3.3 Fiduciary Out. Notwithstanding Section 3.2, Target's Board may respond to an unsolicited Superior Proposal if failure to do so would be inconsistent with its fiduciary duties.

3.4 Termination Fee. If this Agreement is terminated due to Target accepting a Superior Proposal, Target shall pay Acquirer a termination fee of $[X] (the "Termination Fee").

3.5 Regulatory Approvals. Each Party shall use reasonable best efforts to obtain all required regulatory approvals.`,
        annotations: [
          {
            id: 'no-shop',
            term: 'No-Shop',
            type: 'warning',
            title: 'No-Shop Provision',
            importance: 'High - Limits your options',
            tips: [
              'Ensure "fiduciary out" is included',
              'Negotiate "window shop" provisions',
              'Limit duration if possible',
            ],
            redFlag: 'No-shop without fiduciary out',
            example: 'Standard: No-shop + fiduciary out for Superior Proposals',
          },
          {
            id: 'termination-fee',
            term: 'Termination Fee',
            type: 'warning',
            title: 'Break-Up Fee',
            importance: 'High - Cost of accepting better offer',
            tips: [
              'Negotiate lower percentage (2-4% of deal value)',
              'Ensure fee is not payable in all termination scenarios',
              'Consider reverse termination fee',
            ],
            benchmark: 'Typical: 2-4% of equity value',
            example: 'Mutual: Target pays 3% if accepts Superior Proposal; Acquirer pays 6% if fails to close',
          },
        ],
      },
      {
        id: 'article4',
        title: 'ARTICLE 4. INDEMNIFICATION',
        content: `4.1 Survival. Representations and warranties shall survive Closing for [18] months; Fundamental Representations shall survive for [6] years.

4.2 Indemnification by Shareholders. Shareholders shall indemnify Acquirer for Losses arising from breach of representations, warranties, or covenants.

4.3 Limitations.
    (a) Deductible: No indemnification until aggregate Losses exceed $[X] (the "Deductible")
    (b) Cap: Total indemnification shall not exceed the Escrow Amount, except for Fundamental Representations
    (c) Fundamental Representations: Indemnification for Fundamental Representations shall be capped at the Merger Consideration

4.4 Exclusive Remedy. Except for fraud, indemnification shall be the exclusive remedy for breaches of this Agreement.`,
        annotations: [
          {
            id: 'survival',
            term: 'Survival',
            type: 'warning',
            title: 'Survival Periods',
            importance: 'High - Duration of liability',
            tips: [
              'Negotiate shorter survival (12-18 months)',
              'Limit "Fundamental Reps" to truly fundamental items',
              'Include run-off provisions',
            ],
            redFlag: 'Long survival (3+ years) for general reps',
            example: 'Standard: 18 months general; 6 years fundamental (org, cap, authority)',
          },
          {
            id: 'caps',
            term: 'Limitations',
            type: 'critical',
            title: 'Indemnification Caps',
            importance: 'Critical - Limits your exposure',
            tips: [
              'Negotiate meaningful deductible (0.5-1% of deal)',
              'Cap general indemnity at escrow amount',
              'Carve out fraud and willful breach',
            ],
            redFlag: 'No cap or cap at full purchase price',
            example: 'Target: $500K deductible, 10% cap, escrow-only for general claims',
          },
        ],
      },
      {
        id: 'article5',
        title: 'ARTICLE 5. CONDITIONS TO CLOSING',
        content: `5.1 Conditions to Each Party's Obligations.
    (a) Shareholder Approval: Approval by Target shareholders
    (b) Regulatory Approvals: Receipt of all required antitrust and regulatory approvals
    (c) No Injunction: No court order prohibiting the Merger

5.2 Conditions to Acquirer's Obligations.
    (a) Accuracy of Representations: Target's representations true in all material respects
    (b) Covenants: Target has performed all covenants in all material respects
    (c) No Material Adverse Effect: No MAE shall have occurred
    (d) Officer's Certificate: Delivery of closing certificate

5.3 Conditions to Target's Obligations.
    (a) Accuracy of Representations: Acquirer's representations true in all material respects
    (b) Covenants: Acquirer has performed all covenants in all material respects
    (c) Financing: Acquirer has obtained necessary financing
    (d) Officer's Certificate: Delivery of closing certificate`,
        annotations: [
          {
            id: 'mae',
            term: 'Material Adverse Effect',
            type: 'danger',
            title: 'MAE Definition',
            importance: 'Critical - Walk-away right',
            tips: [
              'Negotiate broad carve-outs (industry, economy, pandemic)',
              'Exclude effects of announcement of deal',
              'Include disproportionate impact qualifier',
            ],
            redFlag: 'Narrow MAE definition allows buyer to walk',
            example: 'Carve out: industry conditions, economy, pandemic, deal announcement, failure to meet projections',
          },
          {
            id: 'financing-condition',
            term: 'Financing',
            type: 'warning',
            title: 'Financing Condition',
            importance: 'High - Deal certainty',
            tips: [
              'Prefer no financing condition (committed funds)',
              'If financing condition, require committed financing letters',
              'Include reverse termination fee if financing fails',
            ],
            redFlag: 'Financing condition without reverse break fee',
            example: 'Best: cash deal, no financing condition; if debt, require committed facilities',
          },
        ],
      },
      {
        id: 'article6',
        title: 'ARTICLE 6. EMPLOYEE MATTERS',
        content: `6.1 Continuing Employees. Acquirer shall offer employment to substantially all employees of Target on comparable terms.

6.2 Compensation and Benefits. For [12] months following Closing, continuing employees shall receive:
    (a) Base salary no less than current levels
    (b) Target bonus opportunity no less than current levels
    (c) Employee benefits substantially comparable to current benefits

6.3 Severance. Employees terminated within [12] months of Closing shall receive severance of:
    (a) [X] weeks of base salary per year of service
    (b) [X] months of COBRA subsidy
    (c) Pro-rated bonus for year of termination

6.4 Service Credit. Continuing employees shall receive service credit for prior service with Target.

6.5 Change of Control Payments. Acquirer shall honor all change of control and retention arrangements.`,
        annotations: [
          {
            id: 'employee-protection',
            term: 'Compensation and Benefits',
            type: 'warning',
            title: 'Employee Protections',
            importance: 'High - Team retention',
            tips: [
              'Secure minimum 12-month comparable terms',
              'Include bonus guarantee or true-up',
              'Address equity replacement',
            ],
            redFlag: 'Short protection period or significant benefit cuts',
            example: 'Protect: 12-24 months comparable; bonus at least at target; equity replacement',
          },
          {
            id: 'severance',
            term: 'Severance',
            type: 'info',
            title: 'Severance Terms',
            importance: 'Medium - Employee protection',
            tips: [
              'Negotiate enhanced severance post-closing',
              'Include COBRA subsidy',
              'Address equity vesting acceleration',
            ],
            benchmark: 'Typical: 2-4 weeks per year + 6-12 months COBRA',
            example: 'Enhanced: 4 weeks per year (min 12 weeks) + 12 months COBRA + pro-rata bonus',
          },
        ],
      },
      {
        id: 'article7',
        title: 'ARTICLE 7. TERMINATION',
        content: `7.1 Termination by Mutual Consent. This Agreement may be terminated by mutual written consent.

7.2 Termination by Either Party.
    (a) Outside Date: If the Closing has not occurred by the Outside Date ([12] months from signing)
    (b) Permanent Injunction: If any court issues a final, non-appealable order prohibiting the Merger
    (c) Shareholder Rejection: If Target shareholders fail to approve the Merger

7.3 Termination by Target.
    (a) Acquirer Breach: If Acquirer breaches representations or covenants, not cured within [30] days
    (b) Superior Proposal: If Target enters into agreement for Superior Proposal (subject to Termination Fee)

7.4 Termination by Acquirer.
    (a) Target Breach: If Target breaches representations or covenants, not cured within [30] days
    (b) Change of Recommendation: If Target's Board changes its recommendation

7.5 Effect of Termination. Upon termination, this Agreement shall become void, except for certain surviving provisions.`,
        annotations: [
          {
            id: 'outside-date',
            term: 'Outside Date',
            type: 'warning',
            title: 'Outside Date / Drop Dead Date',
            importance: 'High - Time limit on deal',
            tips: [
              'Allow sufficient time for regulatory approvals',
              'Include extension rights for regulatory delays',
              'Ensure mutual extension rights',
            ],
            redFlag: 'Short outside date with no extension option',
            example: 'Standard: 12 months + automatic 3-month extensions for regulatory delays',
          },
          {
            id: 'superior-proposal',
            term: 'Superior Proposal',
            type: 'info',
            title: 'Superior Proposal Rights',
            importance: 'Medium - Flexibility to accept better offer',
            tips: [
              'Define "Superior Proposal" clearly',
              'Include matching rights for original buyer',
              'Negotiate reasonable termination fee',
            ],
            example: 'Fair: 3 business days matching right; 3% termination fee',
          },
        ],
      },
      {
        id: 'article8',
        title: 'ARTICLE 8. GENERAL PROVISIONS',
        content: `8.1 Expenses. Each Party shall bear its own expenses in connection with this Agreement.

8.2 Amendment. This Agreement may only be amended by written instrument signed by all Parties.

8.3 Governing Law. This Agreement shall be governed by the laws of the State of Delaware.

8.4 Dispute Resolution. Any disputes shall be resolved by arbitration in [City, State].

8.5 Specific Performance. The Parties shall be entitled to specific performance of this Agreement.

8.6 Assignment. This Agreement may not be assigned without the prior written consent of the other Party.

8.7 Entire Agreement. This Agreement constitutes the entire agreement between the Parties.

8.8 Confidentiality. The Parties shall maintain confidentiality of this Agreement and the transactions contemplated herein.`,
        annotations: [
          {
            id: 'specific-performance',
            term: 'Specific Performance',
            type: 'critical',
            title: 'Specific Performance',
            importance: 'Critical - Force deal to close',
            tips: [
              'Ensure Target has right to specific performance',
              'Require Acquirer to close if financing available',
              'Avoid limitations on specific performance remedy',
            ],
            redFlag: 'No specific performance right for Target',
            example: 'Protect: "Target entitled to specific performance; Acquirer must close if financing obtained"',
          },
          {
            id: 'governing-law',
            term: 'Governing Law',
            type: 'info',
            title: 'Choice of Law',
            importance: 'Medium - Determines legal framework',
            tips: [
              'Delaware law is standard for M&A',
              'Consider jurisdiction for disputes',
              'Understand implications for fiduciary duties',
            ],
            example: 'Standard: Delaware law; Delaware Court of Chancery for disputes',
          },
        ],
      },
    ],
  },
  // Option Agreement Template
  option: {
    title: 'OPTION AND LICENSE AGREEMENT',
    parties: {
      licensor: '[LICENSOR]',
      licensee: '[OPTION HOLDER]',
    },
    sections: [
      {
        id: 'recitals',
        title: 'RECITALS',
        content: `WHEREAS, Licensor possesses certain proprietary technology relating to [COMPOUND/TECHNOLOGY];

WHEREAS, Option Holder desires to obtain an exclusive option to acquire a license to develop and commercialize products based on the Technology;

WHEREAS, during the Option Period, the Parties desire to conduct an Evaluation Program;

NOW, THEREFORE, the Parties agree as follows:`,
        annotations: [],
      },
      {
        id: 'article1',
        title: 'ARTICLE 1. DEFINITIONS',
        content: `1.1 "Evaluation Program" means the research and evaluation activities to be conducted during the Option Period.

1.2 "Option" means the exclusive option granted to Option Holder to acquire the License.

1.3 "Option Exercise Fee" means the fee payable upon exercise of the Option, as set forth in Section 3.2.

1.4 "Option Fee" means $[X] payable to Licensor upon execution of this Agreement.

1.5 "Option Period" means the period beginning on the Effective Date and ending [18] months thereafter, unless extended.

1.6 "Pre-Negotiated License Terms" means the license terms set forth in Exhibit A.`,
        annotations: [
          {
            id: 'option-period',
            term: '"Option Period"',
            type: 'critical',
            title: 'Option Period Duration',
            importance: 'Critical - Exclusivity timeline',
            tips: [
              'Balance sufficient evaluation time vs. market opportunity',
              'Include extension provisions with additional fees',
              'Tie extensions to meaningful milestones',
            ],
            redFlag: 'Long option period (2+ years) with no obligation',
            example: 'Standard: 12-18 months; 6-month extensions for $[X] each',
          },
          {
            id: 'prenegotiated',
            term: '"Pre-Negotiated License Terms"',
            type: 'critical',
            title: 'Pre-Negotiated License',
            importance: 'Critical - Future deal terms',
            tips: [
              'Negotiate key license terms upfront (royalties, milestones)',
              'Include complete term sheet in Exhibit',
              'Minimize open items',
            ],
            redFlag: 'Vague license terms leave room for dispute',
            example: 'Include: upfront, milestones, royalty rates, territory, field, termination',
          },
        ],
      },
      {
        id: 'article2',
        title: 'ARTICLE 2. OPTION GRANT',
        content: `2.1 Option Grant. Licensor hereby grants to Option Holder an exclusive option to acquire an exclusive, worldwide license under the Licensed Technology, on the terms set forth in the Pre-Negotiated License Terms.

2.2 Exclusivity. During the Option Period, Licensor shall not grant any rights to the Licensed Technology to any Third Party, nor conduct discussions regarding such grant.

2.3 Evaluation License. During the Option Period, Licensor grants Option Holder a non-exclusive license to evaluate the Licensed Technology solely for purposes of determining whether to exercise the Option.

2.4 No Other Rights. Except as expressly provided, no rights are granted to Option Holder under this Agreement.`,
        annotations: [
          {
            id: 'exclusivity-opt',
            term: 'Exclusivity',
            type: 'warning',
            title: 'Exclusivity During Option',
            importance: 'High - Blocks other deals',
            tips: [
              'Ensure exclusivity is limited to Option Period',
              'Include automatic termination if Option not exercised',
              'Consider higher option fees for longer exclusivity',
            ],
            redFlag: 'Exclusivity continues after option expires',
            example: 'Standard: exclusivity ends immediately upon Option expiration or non-exercise',
          },
          {
            id: 'eval-license',
            term: 'Evaluation License',
            type: 'info',
            title: 'Evaluation Rights',
            importance: 'Medium - Scope of activities',
            tips: [
              'Limit evaluation activities to agreed scope',
              'Require reporting on evaluation progress',
              'Retain ownership of evaluation data',
            ],
            redFlag: 'Broad evaluation rights that amount to a free license',
            example: 'Limit to: "internal evaluation only; no sublicensing; quarterly reports"',
          },
        ],
      },
      {
        id: 'article3',
        title: 'ARTICLE 3. FINANCIAL TERMS',
        content: `3.1 Option Fee. Option Holder shall pay Licensor the Option Fee of $[X] within [10] days of the Effective Date.

3.2 Option Exercise Fee. Upon exercise of the Option, Option Holder shall pay Licensor $[Y].

3.3 Research Funding. During the Option Period, Option Holder shall fund the Evaluation Program at $[Z] per year.

3.4 Non-Refundable. All payments under this Article 3 are non-refundable and non-creditable.

3.5 Crediting. [X]% of the Option Fee and Option Exercise Fee shall be creditable against future milestone payments under the License Agreement.`,
        annotations: [
          {
            id: 'option-fee',
            term: 'Option Fee',
            type: 'info',
            title: 'Option Fee',
            importance: 'Medium - Upfront cash',
            tips: [
              'Price based on exclusivity value and opportunity cost',
              'Consider annual fees for long option periods',
              'Negotiate non-creditable vs. creditable',
            ],
            benchmark: 'Typical: $2-20M depending on stage and opportunity',
            example: 'Structure: $5M upfront + $3M extension fee + $15M exercise fee',
          },
          {
            id: 'research-funding',
            term: 'Research Funding',
            type: 'info',
            title: 'Funded Research',
            importance: 'Medium - Non-dilutive capital',
            tips: [
              'Ensure funding covers your costs plus margin',
              'Define scope of funded activities clearly',
              'Retain IP from funded research',
            ],
            redFlag: 'Research funding below your cost',
            example: 'Cover: FTE costs at market rates + direct costs + 25% overhead',
          },
          {
            id: 'crediting',
            term: 'Crediting',
            type: 'warning',
            title: 'Payment Credits',
            importance: 'High - Reduces future payments',
            tips: [
              'Minimize or eliminate crediting',
              'If crediting, cap at percentage of future payments',
              'Never credit against royalties',
            ],
            redFlag: 'Full crediting reduces your total deal value',
            example: 'Limit: "25% of Option Fee creditable against first milestone only"',
          },
        ],
      },
      {
        id: 'article4',
        title: 'ARTICLE 4. OPTION EXERCISE',
        content: `4.1 Exercise Notice. Option Holder may exercise the Option by providing written notice to Licensor at any time during the Option Period.

4.2 License Agreement. Within [30] days of exercise, the Parties shall execute the License Agreement on the Pre-Negotiated License Terms.

4.3 Effect of Non-Exercise. If Option Holder does not exercise the Option by the end of the Option Period:
    (a) This Agreement shall terminate
    (b) All rights granted hereunder shall revert to Licensor
    (c) Option Holder shall return all Confidential Information
    (d) Licensor shall be free to grant rights to Third Parties

4.4 No Obligation. Option Holder is under no obligation to exercise the Option.`,
        annotations: [
          {
            id: 'non-exercise',
            term: 'Effect of Non-Exercise',
            type: 'critical',
            title: 'Rights Reversion',
            importance: 'Critical - Get your asset back',
            tips: [
              'Ensure clean reversion with no residual rights',
              'Require return of data and materials',
              'Include non-compete/non-use provisions',
            ],
            redFlag: 'Partner retains any rights after non-exercise',
            example: 'Full reversion: all rights return; all data returned; 2-year non-compete',
          },
        ],
      },
      {
        id: 'article5',
        title: 'ARTICLE 5. EVALUATION PROGRAM',
        content: `5.1 Evaluation Activities. During the Option Period, the Parties shall conduct an Evaluation Program as set forth in the Evaluation Plan.

5.2 Evaluation Plan. The Parties shall agree on an Evaluation Plan within [30] days of the Effective Date.

5.3 Reporting. Option Holder shall provide quarterly reports on the progress of the Evaluation Program.

5.4 Data Sharing. Licensor shall provide Option Holder with reasonable access to data and materials for evaluation purposes.

5.5 Evaluation Results. All results generated during the Evaluation Program shall be owned by Licensor.

5.6 Diligence. Option Holder shall conduct the Evaluation Program with reasonable diligence.`,
        annotations: [
          {
            id: 'eval-results',
            term: 'Evaluation Results',
            type: 'critical',
            title: 'Evaluation Data Ownership',
            importance: 'Critical - Protects your data',
            tips: [
              'Retain ownership of all evaluation data',
              'Limit Option Holder use to evaluation only',
              'Require return of all data upon expiration',
            ],
            redFlag: 'Option Holder owns or retains evaluation data',
            example: 'Protect: "All data owned by Licensor; Option Holder has use right during Option Period only"',
          },
          {
            id: 'diligence-opt',
            term: 'Diligence',
            type: 'warning',
            title: 'Evaluation Diligence',
            importance: 'High - Ensures progress',
            tips: [
              'Require minimum FTE commitment',
              'Include milestone-based progress requirements',
              'Early termination right if no diligence',
            ],
            redFlag: 'No diligence obligation allows warehousing',
            example: 'Require: "Minimum [X] FTEs; quarterly milestone report; early termination for non-diligence"',
          },
        ],
      },
      {
        id: 'article6',
        title: 'ARTICLE 6. INTELLECTUAL PROPERTY',
        content: `6.1 Background IP. Licensor retains all rights in its Background IP and the Licensed Technology.

6.2 Evaluation IP. All inventions arising from the Evaluation Program shall be owned by Licensor.

6.3 Improvements. Any improvements to the Licensed Technology made by Option Holder shall be assigned to Licensor.

6.4 Patent Filing. Licensor shall have the right to file patents on any Evaluation IP.

6.5 No License. Except as expressly provided, no license is granted under any intellectual property.`,
        annotations: [
          {
            id: 'improvements-opt',
            term: 'Improvements',
            type: 'critical',
            title: 'Improvement Ownership',
            importance: 'Critical - Protects your technology',
            tips: [
              'All improvements should flow to you',
              'Require assignment, not just license',
              'Include assistance with prosecution',
            ],
            redFlag: 'Option Holder keeps improvements after non-exercise',
            example: 'Protect: "All improvements assigned to Licensor; Option Holder provides prosecution assistance"',
          },
        ],
      },
      {
        id: 'article7',
        title: 'ARTICLE 7. CONFIDENTIALITY',
        content: `7.1 Confidential Information. Each Party shall maintain the confidentiality of the other Party's Confidential Information.

7.2 Use Restriction. Confidential Information may only be used for purposes of this Agreement.

7.3 Exceptions. Confidential Information does not include information that:
    (a) Is or becomes publicly available
    (b) Was known prior to disclosure
    (c) Is independently developed
    (d) Is disclosed by a Third Party without restriction

7.4 Term. Confidentiality obligations shall survive for [10] years after termination.

7.5 Return of Information. Upon termination, each Party shall return or destroy Confidential Information.`,
        annotations: [
          {
            id: 'use-restriction',
            term: 'Use Restriction',
            type: 'warning',
            title: 'Use Limitations',
            importance: 'High - Prevents misuse',
            tips: [
              'Strictly limit use to evaluation purposes',
              'Prohibit use for competing programs',
              'Include non-compete provisions',
            ],
            redFlag: 'Broad use rights that enable competition',
            example: 'Restrict: "Use solely for Evaluation; no use for competing programs for [X] years"',
          },
          {
            id: 'survival-conf',
            term: 'Confidentiality obligations shall survive',
            type: 'info',
            title: 'Survival Period',
            importance: 'Medium - Long-term protection',
            tips: [
              'Standard is 5-10 years',
              'Trade secrets should be perpetual',
              'Include ongoing non-use obligations',
            ],
            example: 'Standard: 10 years general; perpetual for trade secrets',
          },
        ],
      },
      {
        id: 'article8',
        title: 'ARTICLE 8. TERM AND TERMINATION',
        content: `8.1 Term. This Agreement shall commence on the Effective Date and continue until the earlier of (a) exercise of the Option, (b) expiration of the Option Period, or (c) termination.

8.2 Termination for Cause. Either Party may terminate for material breach if not cured within [30] days.

8.3 Termination by Licensor. Licensor may terminate if Option Holder fails to meet diligence requirements.

8.4 Effects of Termination.
    (a) All rights granted to Option Holder shall terminate
    (b) Option Holder shall return all materials and Confidential Information
    (c) Option Holder shall assign all Evaluation IP to Licensor
    (d) Confidentiality obligations shall survive

8.5 No Refund. Upon termination, no fees paid shall be refunded.

8.6 Non-Compete. For [2] years following termination, Option Holder shall not develop products competitive to the Licensed Technology.`,
        annotations: [
          {
            id: 'non-compete',
            term: 'Non-Compete',
            type: 'critical',
            title: 'Post-Termination Non-Compete',
            importance: 'Critical - Prevents competition',
            tips: [
              'Include meaningful non-compete period',
              'Cover specific therapeutic areas',
              'Address use of learned information',
            ],
            redFlag: 'No non-compete allows immediate competition',
            example: 'Protect: "2-year non-compete in [Field]; no use of Confidential Information for competing products"',
          },
          {
            id: 'ip-assignment',
            term: 'assign all Evaluation IP',
            type: 'critical',
            title: 'IP Assignment on Termination',
            importance: 'Critical - Get your IP back',
            tips: [
              'Automatic assignment upon termination',
              'Include power of attorney for assignments',
              'Require ongoing cooperation',
            ],
            redFlag: 'IP remains with Option Holder after termination',
            example: 'Auto-assign: "All IP automatically assigned; Option Holder grants POA for filings"',
          },
        ],
      },
    ],
  },
};

export default function BDResources() {
  const [activeSection, setActiveSection] = useState('types');
  const [expandedType, setExpandedType] = useState('licensing');
  const [selectedAnnotation, setSelectedAnnotation] = useState(null);
  const [activeArticle, setActiveArticle] = useState('article1');
  const [selectedContractType, setSelectedContractType] = useState(null);
  const [selectedAmendmentAnnotation, setSelectedAmendmentAnnotation] = useState(null);
  const [activeAmendmentSection, setActiveAmendmentSection] = useState('preamble');

  const sections = [
    { id: 'types', name: 'Deal Types', icon: GitBranch },
    { id: 'economics', name: 'Deal Economics', icon: DollarSign },
    { id: 'benchmarks', name: 'Deal Benchmarks', icon: BarChart3 },
    { id: 'process', name: 'Deal Process', icon: Clock },
    { id: 'checklist', name: 'Preparation Checklist', icon: CheckSquare },
    { id: 'comps', name: 'Recent Deal Comps', icon: TrendingUp },
    { id: 'contracts', name: 'Key Contract Terms', icon: Scale },
    { id: 'amendments', name: 'Amendment Guide', icon: Edit2 },
    { id: 'exit', name: 'Exit Scenarios', icon: LogOut },
    { id: 'china', name: 'China Compliance', icon: ShieldCheck },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-xl p-6 text-white">
        <div className="flex items-center gap-3 mb-2">
          <BookOpen className="w-8 h-8" />
          <h1 className="text-2xl font-bold">BD Resources</h1>
        </div>
        <p className="text-purple-100 max-w-2xl">
          Everything you need to understand pharma BD deals. From deal structures to market benchmarks,
          prepare yourself for successful partnering conversations.
        </p>
      </div>

      {/* Section Navigation */}
      <div className="bg-white rounded-xl border border-gray-200 p-1 flex flex-wrap gap-1">
        {sections.map((section) => (
          <button
            key={section.id}
            onClick={() => setActiveSection(section.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeSection === section.id
                ? 'bg-purple-100 text-purple-700'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <section.icon className="w-4 h-4" />
            {section.name}
          </button>
        ))}
      </div>

      {/* Deal Types Section */}
      {activeSection === 'types' && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Understanding Deal Types</h2>
            <p className="text-gray-600 mb-6">
              Each deal type has different implications for control, economics, and risk. Choose based on your
              company's stage, capital needs, and long-term strategy.
            </p>

            <div className="space-y-4">
              {dealTypes.map((type) => (
                <div
                  key={type.id}
                  className={`border rounded-lg overflow-hidden ${
                    expandedType === type.id ? 'border-purple-300 bg-purple-50/30' : 'border-gray-200'
                  }`}
                >
                  <button
                    onClick={() => setExpandedType(expandedType === type.id ? null : type.id)}
                    className="w-full px-4 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg bg-${type.color}-100 flex items-center justify-center`}>
                        <type.icon className={`w-5 h-5 text-${type.color}-600`} />
                      </div>
                      <div className="text-left">
                        <h3 className="font-medium text-gray-900">{type.name}</h3>
                        <p className="text-sm text-gray-500">{type.description}</p>
                      </div>
                    </div>
                    {expandedType === type.id ? (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    )}
                  </button>

                  {expandedType === type.id && (
                    <div className="px-4 pb-4 space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="bg-white rounded-lg p-4 border border-gray-200">
                          <h4 className="font-medium text-gray-900 mb-2">Key Terms</h4>
                          <ul className="space-y-1">
                            {type.keyTerms.map((term, i) => (
                              <li key={i} className="text-sm text-gray-600 flex items-center gap-2">
                                <span className="w-1.5 h-1.5 bg-purple-500 rounded-full" />
                                {term}
                              </li>
                            ))}
                          </ul>
                        </div>

                        <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                          <h4 className="font-medium text-green-800 mb-2">Pros</h4>
                          <ul className="space-y-1">
                            {type.pros.map((pro, i) => (
                              <li key={i} className="text-sm text-green-700 flex items-start gap-2">
                                <span className="mt-1.5 w-1.5 h-1.5 bg-green-500 rounded-full flex-shrink-0" />
                                {pro}
                              </li>
                            ))}
                          </ul>
                        </div>

                        <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                          <h4 className="font-medium text-red-800 mb-2">Cons</h4>
                          <ul className="space-y-1">
                            {type.cons.map((con, i) => (
                              <li key={i} className="text-sm text-red-700 flex items-start gap-2">
                                <span className="mt-1.5 w-1.5 h-1.5 bg-red-500 rounded-full flex-shrink-0" />
                                {con}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>

                      <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                        <div className="flex items-start gap-3">
                          <Lightbulb className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                          <div>
                            <h4 className="font-medium text-blue-800 mb-1">Example Deal</h4>
                            <p className="text-sm text-blue-700 font-medium">{type.example.deal}</p>
                            <p className="text-sm text-blue-600">
                              Total Value: {type.example.value} | Structure: {type.example.structure}
                            </p>
                          </div>
                        </div>
                      </div>

                      <p className="text-sm text-gray-500">
                        <span className="font-medium">Typical Use Case:</span> {type.typical}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Deal Economics Section */}
      {activeSection === 'economics' && (
        <div className="space-y-6">
          {/* Upfront */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">{economicsData.upfront.title}</h2>
                <p className="text-sm text-gray-500">{economicsData.upfront.description}</p>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Stage</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Range</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Median</th>
                  </tr>
                </thead>
                <tbody>
                  {economicsData.upfront.benchmarks.map((row, i) => (
                    <tr key={i} className="border-b border-gray-100">
                      <td className="py-3 px-4 text-sm font-medium text-gray-900">{row.stage}</td>
                      <td className="py-3 px-4 text-sm text-gray-600">{row.range}</td>
                      <td className="py-3 px-4 text-sm text-green-600 font-medium">{row.median}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-4 bg-yellow-50 rounded-lg p-4 border border-yellow-200">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-medium text-yellow-800 mb-1">Pro Tips</h4>
                  <ul className="space-y-1">
                    {economicsData.upfront.tips.map((tip, i) => (
                      <li key={i} className="text-sm text-yellow-700">• {tip}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Milestones */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">{economicsData.milestones.title}</h2>
                <p className="text-sm text-gray-500">{economicsData.milestones.description}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {economicsData.milestones.categories.map((cat, i) => (
                <div key={i} className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-2">{cat.name}</h4>
                  <ul className="space-y-1 mb-3">
                    {cat.examples.map((ex, j) => (
                      <li key={j} className="text-sm text-gray-600">• {ex}</li>
                    ))}
                  </ul>
                  <p className="text-xs text-purple-600 font-medium">{cat.typical}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Royalties */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">{economicsData.royalties.title}</h2>
                <p className="text-sm text-gray-500">{economicsData.royalties.description}</p>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Stage at Deal</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Royalty Range</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Typical</th>
                  </tr>
                </thead>
                <tbody>
                  {economicsData.royalties.benchmarks.map((row, i) => (
                    <tr key={i} className="border-b border-gray-100">
                      <td className="py-3 px-4 text-sm font-medium text-gray-900">{row.stage}</td>
                      <td className="py-3 px-4 text-sm text-gray-600">{row.range}</td>
                      <td className="py-3 px-4 text-sm text-purple-600 font-medium">{row.typical}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Deal Benchmarks Section */}
      {activeSection === 'benchmarks' && (
        <div className="space-y-6">
          {/* Summary Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="text-2xl font-bold text-gray-900">{dealBenchmarksData.summary.totalDeals}</div>
              <div className="text-sm text-gray-500">Total Deals Analyzed</div>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="text-2xl font-bold text-green-600">${dealBenchmarksData.summary.avgUpfront}M</div>
              <div className="text-sm text-gray-500">Avg Upfront Payment</div>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="text-2xl font-bold text-blue-600">${dealBenchmarksData.summary.avgTotal}M</div>
              <div className="text-sm text-gray-500">Avg Total Value</div>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="text-2xl font-bold text-purple-600">{dealBenchmarksData.summary.timeframe}</div>
              <div className="text-sm text-gray-500">Data Timeframe</div>
            </div>
          </div>

          {/* By Therapeutic Area */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-indigo-600" />
              Benchmarks by Therapeutic Area
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-2 font-medium text-gray-600">Therapeutic Area</th>
                    <th className="text-right py-3 px-2 font-medium text-gray-600">Deals</th>
                    <th className="text-right py-3 px-2 font-medium text-gray-600">Avg Upfront</th>
                    <th className="text-right py-3 px-2 font-medium text-gray-600">Avg Total</th>
                    <th className="text-right py-3 px-2 font-medium text-gray-600">Max Upfront</th>
                    <th className="text-center py-3 px-2 font-medium text-gray-600">Trend</th>
                  </tr>
                </thead>
                <tbody>
                  {dealBenchmarksData.byTherapeuticArea.map((row, idx) => (
                    <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-2 font-medium text-gray-900">{row.area}</td>
                      <td className="py-3 px-2 text-right text-gray-600">{row.deals}</td>
                      <td className="py-3 px-2 text-right text-green-600 font-medium">${row.avgUpfront}M</td>
                      <td className="py-3 px-2 text-right text-blue-600">${row.avgTotal}M</td>
                      <td className="py-3 px-2 text-right text-purple-600">${row.maxUpfront}M</td>
                      <td className="py-3 px-2 text-center">
                        {row.trend === 'up' && <ArrowUpRight className="w-4 h-4 text-green-500 inline" />}
                        {row.trend === 'down' && <ArrowDownRight className="w-4 h-4 text-red-500 inline" />}
                        {row.trend === 'stable' && <span className="text-gray-400">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* By Modality */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-green-600" />
              Benchmarks by Modality (Hot Platforms Highlighted)
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-2 font-medium text-gray-600">Modality</th>
                    <th className="text-right py-3 px-2 font-medium text-gray-600">Deals</th>
                    <th className="text-right py-3 px-2 font-medium text-gray-600">Avg Upfront</th>
                    <th className="text-right py-3 px-2 font-medium text-gray-600">Avg Total</th>
                    <th className="text-right py-3 px-2 font-medium text-gray-600">Premium vs Avg</th>
                  </tr>
                </thead>
                <tbody>
                  {dealBenchmarksData.byModality.map((row, idx) => (
                    <tr key={idx} className={`border-b border-gray-100 hover:bg-gray-50 ${row.hot ? 'bg-amber-50' : ''}`}>
                      <td className="py-3 px-2 font-medium text-gray-900 flex items-center gap-2">
                        {row.modality}
                        {row.hot && <span className="text-xs bg-amber-500 text-white px-1.5 py-0.5 rounded">HOT</span>}
                      </td>
                      <td className="py-3 px-2 text-right text-gray-600">{row.deals}</td>
                      <td className="py-3 px-2 text-right text-green-600 font-medium">${row.avgUpfront}M</td>
                      <td className="py-3 px-2 text-right text-blue-600">${row.avgTotal}M</td>
                      <td className={`py-3 px-2 text-right font-medium ${row.premiumVsAvg.startsWith('+') ? 'text-green-600' : 'text-red-500'}`}>
                        {row.premiumVsAvg}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Upfront Ratios by Stage */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-600" />
              Upfront Payment Ratios by Development Stage
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              {dealBenchmarksData.upfrontRatios.map((stage, idx) => (
                <div key={idx} className="bg-gradient-to-br from-gray-50 to-white rounded-lg border border-gray-200 p-4">
                  <div className="text-sm font-medium text-gray-900 mb-2">{stage.stage}</div>
                  <div className="text-2xl font-bold text-indigo-600 mb-1">{stage.avgRatio}</div>
                  <div className="text-xs text-gray-500 mb-2">{stage.range}</div>
                  <div className="text-xs text-gray-400">{stage.note}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Recent Mega Deals */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-purple-600" />
              Recent Mega Deals (2025)
            </h3>
            <div className="space-y-3">
              {dealBenchmarksData.recentMegaDeals.map((deal, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">
                      {deal.buyer} <span className="text-gray-400">+</span> {deal.seller}
                    </div>
                    <div className="text-sm text-gray-500">{deal.area} | {deal.modality}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-green-600">${deal.upfront}M upfront</div>
                    <div className="text-sm text-blue-600">${deal.total}M total</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Market Insights */}
          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl border border-indigo-200 p-6">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-amber-500" />
              Market Insights
            </h3>
            <div className="space-y-3">
              {dealBenchmarksData.insights.map((insight, idx) => (
                <div key={idx} className={`flex items-start gap-3 p-3 rounded-lg ${
                  insight.type === 'positive' ? 'bg-green-50 border border-green-200' :
                  insight.type === 'warning' ? 'bg-amber-50 border border-amber-200' :
                  'bg-white border border-gray-200'
                }`}>
                  {insight.type === 'positive' && <ArrowUpRight className="w-5 h-5 text-green-600 mt-0.5" />}
                  {insight.type === 'warning' && <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />}
                  {insight.type === 'neutral' && <Lightbulb className="w-5 h-5 text-blue-600 mt-0.5" />}
                  <span className="text-sm text-gray-700">{insight.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Deal Process Section */}
      {activeSection === 'process' && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Typical Deal Timeline</h2>
          <p className="text-gray-600 mb-6">
            A typical licensing deal takes 4-9 months from first meeting to signing. M&A can take 6-12+ months.
          </p>

          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-6 top-8 bottom-8 w-0.5 bg-purple-200" />

            <div className="space-y-6">
              {dealProcess.map((step, i) => (
                <div key={i} className="relative flex gap-4">
                  <div className="w-12 h-12 rounded-full bg-purple-600 flex items-center justify-center text-white font-bold flex-shrink-0 z-10">
                    {step.step}
                  </div>
                  <div className="flex-1 bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-medium text-gray-900">{step.name}</h3>
                      <span className="text-sm text-purple-600 font-medium">{step.duration}</span>
                    </div>
                    <p className="text-sm text-gray-600 mb-3">{step.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {step.deliverables.map((d, j) => (
                        <span key={j} className="text-xs bg-white border border-gray-200 rounded-full px-3 py-1 text-gray-600">
                          {d}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-6 bg-blue-50 rounded-lg p-4 border border-blue-200">
            <div className="flex items-start gap-3">
              <Lightbulb className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-medium text-blue-800 mb-1">Speed Tips</h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• Have a clean data room ready before starting outreach</li>
                  <li>• Respond to DD requests within 48 hours</li>
                  <li>• Align internal stakeholders (board, key employees) early</li>
                  <li>• Consider running a competitive process to create urgency</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preparation Checklist Section */}
      {activeSection === 'checklist' && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Deal Preparation Checklist</h2>
          <p className="text-gray-600 mb-6">
            Prepare these materials before engaging with potential partners. Items marked with a star are critical.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {preparationChecklist.map((category, i) => (
              <div key={i} className="border border-gray-200 rounded-lg p-4">
                <h3 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                  <CheckSquare className="w-5 h-5 text-purple-600" />
                  {category.category}
                </h3>
                <ul className="space-y-2">
                  {category.items.map((item, j) => (
                    <li key={j} className="flex items-start gap-2">
                      <input type="checkbox" className="mt-1 rounded border-gray-300 text-purple-600 focus:ring-purple-500" />
                      <span className={`text-sm ${item.critical ? 'text-gray-900' : 'text-gray-600'}`}>
                        {item.name}
                        {item.critical && <span className="text-red-500 ml-1">*</span>}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="mt-6 bg-yellow-50 rounded-lg p-4 border border-yellow-200">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-yellow-700">
                <span className="font-medium">Important:</span> Items marked with * are typically required
                before a partner will issue a term sheet. Start preparing these early.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Recent Deal Comps Section */}
      {activeSection === 'comps' && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Recent Deal Comparables</h2>
          <p className="text-gray-600 mb-6">
            Reference deals from the past 12 months. Use these to benchmark your own expectations.
          </p>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Date</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Seller</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Buyer</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Asset</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Stage</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Upfront</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Total</th>
                </tr>
              </thead>
              <tbody>
                {recentDeals.map((deal, i) => (
                  <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4 text-sm text-gray-500">{deal.date}</td>
                    <td className="py-3 px-4 text-sm font-medium text-gray-900">{deal.seller}</td>
                    <td className="py-3 px-4 text-sm text-gray-900">{deal.buyer}</td>
                    <td className="py-3 px-4 text-sm text-gray-600 max-w-[200px] truncate" title={deal.asset}>
                      {deal.asset}
                    </td>
                    <td className="py-3 px-4">
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        deal.stage === 'Preclinical' ? 'bg-gray-100 text-gray-600' :
                        deal.stage === 'Phase 1' ? 'bg-blue-100 text-blue-600' :
                        deal.stage === 'Phase 2' ? 'bg-purple-100 text-purple-600' :
                        'bg-green-100 text-green-600'
                      }`}>
                        {deal.stage}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm text-green-600 font-medium">{deal.upfront}</td>
                    <td className="py-3 px-4 text-sm text-purple-600 font-medium">{deal.total}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-4 text-sm text-gray-500 flex items-center gap-2">
            <ExternalLink className="w-4 h-4" />
            Source: Public disclosures, SEC filings, press releases
          </div>
        </div>
      )}

      {/* Key Contract Terms Section - Interactive Contract Template */}
      {activeSection === 'contracts' && (
        <div className="space-y-4">
          {/* Contract Type Selection View */}
          {!selectedContractType ? (
            <>
              {/* Header */}
              <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl p-6 text-white">
                <div className="flex items-center gap-3 mb-2">
                  <Scale className="w-8 h-8" />
                  <h2 className="text-xl font-bold">BD Contract Templates</h2>
                </div>
                <p className="text-indigo-100">
                  Select a contract type to explore key terms, negotiation tips, and red flags.
                  Each template includes annotated clauses from real pharma/biotech deals.
                </p>
              </div>

              {/* Contract Type Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {contractTypes.map((type) => (
                  <button
                    key={type.id}
                    onClick={() => {
                      setSelectedContractType(type.id);
                      setActiveArticle('article1');
                      setSelectedAnnotation(null);
                    }}
                    className={`text-left bg-white rounded-xl border-2 p-5 transition-all hover:shadow-lg ${
                      type.color === 'blue' ? 'border-blue-200 hover:border-blue-400' :
                      type.color === 'green' ? 'border-green-200 hover:border-green-400' :
                      type.color === 'purple' ? 'border-purple-200 hover:border-purple-400' :
                      'border-orange-200 hover:border-orange-400'
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`text-3xl w-14 h-14 rounded-xl flex items-center justify-center ${
                        type.color === 'blue' ? 'bg-blue-100' :
                        type.color === 'green' ? 'bg-green-100' :
                        type.color === 'purple' ? 'bg-purple-100' :
                        'bg-orange-100'
                      }`}>
                        {type.icon}
                      </div>
                      <div className="flex-1">
                        <h3 className="font-bold text-gray-900 text-lg">{type.name}</h3>
                        <p className="text-sm text-gray-500 mb-2">{type.subtitle}</p>
                        <p className="text-sm text-gray-600 mb-3">{type.description}</p>

                        <div className="mb-3">
                          <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Best For:</p>
                          <div className="flex flex-wrap gap-1">
                            {type.bestFor.map((item, i) => (
                              <span key={i} className={`text-xs px-2 py-0.5 rounded-full ${
                                type.color === 'blue' ? 'bg-blue-50 text-blue-700' :
                                type.color === 'green' ? 'bg-green-50 text-green-700' :
                                type.color === 'purple' ? 'bg-purple-50 text-purple-700' :
                                'bg-orange-50 text-orange-700'
                              }`}>
                                {item}
                              </span>
                            ))}
                          </div>
                        </div>

                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs text-gray-400">Typical Value</p>
                            <p className="text-sm font-medium text-gray-700">{type.typicalValue}</p>
                          </div>
                          <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                            type.color === 'blue' ? 'bg-blue-100 text-blue-700' :
                            type.color === 'green' ? 'bg-green-100 text-green-700' :
                            type.color === 'purple' ? 'bg-purple-100 text-purple-700' :
                            'bg-orange-100 text-orange-700'
                          }`}>
                            View Template →
                          </div>
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>

              {/* Example Deals */}
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                <h3 className="font-semibold text-gray-900 mb-3">Recent Example Deals by Type</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  {contractTypes.map((type) => (
                    <div key={type.id}>
                      <p className="font-medium text-gray-700 mb-1">{type.name.split(' ')[0]}</p>
                      <ul className="text-xs text-gray-500 space-y-0.5">
                        {type.exampleDeals.map((deal, i) => (
                          <li key={i}>• {deal}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : (
            /* Contract Template View */
            <>
              {/* Header with Back Button */}
              <div className={`rounded-xl p-4 text-white ${
                selectedContractType === 'license' ? 'bg-gradient-to-r from-blue-600 to-blue-700' :
                selectedContractType === 'collaboration' ? 'bg-gradient-to-r from-green-600 to-green-700' :
                selectedContractType === 'manda' ? 'bg-gradient-to-r from-purple-600 to-purple-700' :
                'bg-gradient-to-r from-orange-600 to-orange-700'
              }`}>
                <div className="flex items-center gap-3 mb-2">
                  <button
                    onClick={() => {
                      setSelectedContractType(null);
                      setSelectedAnnotation(null);
                    }}
                    className="p-1 hover:bg-white/20 rounded-lg transition-colors"
                  >
                    <ChevronRight className="w-5 h-5 rotate-180" />
                  </button>
                  <span className="text-2xl">{contractTypes.find(t => t.id === selectedContractType)?.icon}</span>
                  <div>
                    <h2 className="text-lg font-bold">{contractTypes.find(t => t.id === selectedContractType)?.name}</h2>
                    <p className="text-sm opacity-80">Click highlighted terms to see guidance in sidebar</p>
                  </div>
                </div>
              </div>

              {/* Quick Switch Tabs */}
              <div className="bg-white rounded-lg border border-gray-200 p-1 flex gap-1">
                {contractTypes.map((type) => (
                  <button
                    key={type.id}
                    onClick={() => {
                      setSelectedContractType(type.id);
                      setActiveArticle('article1');
                      setSelectedAnnotation(null);
                    }}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                      selectedContractType === type.id
                        ? type.color === 'blue' ? 'bg-blue-100 text-blue-700' :
                          type.color === 'green' ? 'bg-green-100 text-green-700' :
                          type.color === 'purple' ? 'bg-purple-100 text-purple-700' :
                          'bg-orange-100 text-orange-700'
                        : 'text-gray-500 hover:bg-gray-100'
                    }`}
                  >
                    <span>{type.icon}</span>
                    {type.name.split(' ')[0]}
                  </button>
                ))}
              </div>

              {/* Article Navigation - Horizontal Tabs */}
              <div className="bg-white rounded-lg border border-gray-200 p-2 overflow-x-auto">
                <div className="flex gap-1 min-w-max">
                  {contractTemplates[selectedContractType]?.sections.map((section, idx) => (
                    <button
                      key={section.id}
                      onClick={() => {
                        setActiveArticle(section.id);
                        setSelectedAnnotation(null);
                      }}
                      className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors whitespace-nowrap flex items-center gap-1.5 ${
                        activeArticle === section.id
                          ? 'bg-indigo-600 text-white shadow-sm'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
                        activeArticle === section.id
                          ? 'bg-white/20'
                          : 'bg-gray-200'
                      }`}>
                        {idx + 1}
                      </span>
                      {section.title.replace(/^ARTICLE \d+\.\s*/, '').replace(/^SECTION \d+\.\s*/, '')}
                    </button>
                  ))}
                </div>
              </div>

              {/* Main Content: Contract + Sidebar */}
              <div className="flex gap-4">
                {/* Contract Document */}
                <div className="flex-1 bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                  {/* Document Header */}
                  <div className="bg-gray-50 border-b border-gray-200 px-6 py-4 text-center">
                    <h3 className="text-lg font-bold text-gray-900 tracking-wide">{contractTemplates[selectedContractType]?.title}</h3>
                    <p className="text-sm text-gray-500 mt-1">
                      by and between {contractTemplates[selectedContractType]?.parties.licensor} and {contractTemplates[selectedContractType]?.parties.licensee}
                    </p>
                  </div>

                  {/* Document Content */}
                  <div className="p-6 max-h-[600px] overflow-y-auto">
                    {contractTemplates[selectedContractType]?.sections
                      .filter((s) => s.id === activeArticle)
                      .map((section) => (
                        <div key={section.id}>
                          <h4 className="text-base font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">
                            {section.title}
                          </h4>
                          <div className="font-serif text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                            {section.annotations.length > 0 ? (
                              // Render content with highlighted terms
                              (() => {
                                let content = section.content;
                                const parts = [];
                                let lastIndex = 0;

                                // Sort annotations by position in content
                                const sortedAnnotations = [...section.annotations].sort((a, b) => {
                                  const posA = content.indexOf(a.term);
                                  const posB = content.indexOf(b.term);
                                  return posA - posB;
                                });

                                sortedAnnotations.forEach((annotation) => {
                                  const termIndex = content.indexOf(annotation.term, lastIndex);
                                  if (termIndex !== -1) {
                                    // Add text before the term
                                    if (termIndex > lastIndex) {
                                      parts.push(
                                        <span key={`text-${lastIndex}`}>
                                          {content.slice(lastIndex, termIndex)}
                                        </span>
                                      );
                                    }
                                    // Add highlighted term
                                    parts.push(
                                      <button
                                        key={annotation.id}
                                        onClick={() => setSelectedAnnotation(annotation)}
                                        className={`relative inline px-0.5 rounded cursor-pointer transition-all ${
                                          annotation.type === 'critical'
                                            ? 'bg-red-100 hover:bg-red-200 border-b-2 border-red-400'
                                            : annotation.type === 'danger'
                                            ? 'bg-orange-100 hover:bg-orange-200 border-b-2 border-orange-400'
                                            : annotation.type === 'warning'
                                            ? 'bg-yellow-100 hover:bg-yellow-200 border-b-2 border-yellow-400'
                                            : 'bg-blue-100 hover:bg-blue-200 border-b-2 border-blue-400'
                                        } ${selectedAnnotation?.id === annotation.id ? 'ring-2 ring-indigo-500' : ''}`}
                                      >
                                        {annotation.term}
                                      </button>
                                    );
                                    lastIndex = termIndex + annotation.term.length;
                                  }
                                });

                                // Add remaining text
                                if (lastIndex < content.length) {
                                  parts.push(
                                    <span key={`text-end`}>{content.slice(lastIndex)}</span>
                                  );
                                }

                                return parts;
                              })()
                            ) : (
                              section.content
                            )}
                          </div>
                        </div>
                      ))}
                  </div>

              {/* Legend */}
              <div className="bg-gray-50 border-t border-gray-200 px-6 py-3">
                <div className="flex flex-wrap gap-4 text-xs">
                  <span className="flex items-center gap-1">
                    <span className="w-3 h-3 bg-red-100 border-b-2 border-red-400 rounded"></span>
                    Critical
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-3 h-3 bg-orange-100 border-b-2 border-orange-400 rounded"></span>
                    High Risk
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-3 h-3 bg-yellow-100 border-b-2 border-yellow-400 rounded"></span>
                    Watch Out
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-3 h-3 bg-blue-100 border-b-2 border-blue-400 rounded"></span>
                    Info
                  </span>
                </div>
              </div>
            </div>

            {/* Sidebar - Annotation Details */}
            <div className="w-80 flex-shrink-0">
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm sticky top-4">
                {selectedAnnotation ? (
                  <div className="p-4">
                    {/* Header */}
                    <div className={`flex items-start gap-3 pb-3 border-b mb-3 ${
                      selectedAnnotation.type === 'critical' ? 'border-red-200' :
                      selectedAnnotation.type === 'danger' ? 'border-orange-200' :
                      selectedAnnotation.type === 'warning' ? 'border-yellow-200' :
                      'border-blue-200'
                    }`}>
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        selectedAnnotation.type === 'critical' ? 'bg-red-100' :
                        selectedAnnotation.type === 'danger' ? 'bg-orange-100' :
                        selectedAnnotation.type === 'warning' ? 'bg-yellow-100' :
                        'bg-blue-100'
                      }`}>
                        {selectedAnnotation.type === 'critical' || selectedAnnotation.type === 'danger' ? (
                          <AlertTriangle className={`w-4 h-4 ${
                            selectedAnnotation.type === 'critical' ? 'text-red-600' : 'text-orange-600'
                          }`} />
                        ) : selectedAnnotation.type === 'warning' ? (
                          <AlertCircle className="w-4 h-4 text-yellow-600" />
                        ) : (
                          <Lightbulb className="w-4 h-4 text-blue-600" />
                        )}
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 text-sm">{selectedAnnotation.title}</h4>
                        <p className="text-xs text-gray-500 mt-0.5">{selectedAnnotation.importance}</p>
                      </div>
                    </div>

                    {/* Tips */}
                    <div className="mb-3">
                      <h5 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
                        Negotiation Tips
                      </h5>
                      <ul className="space-y-1.5">
                        {selectedAnnotation.tips.map((tip, i) => (
                          <li key={i} className="text-xs text-gray-600 flex items-start gap-2">
                            <ChevronRight className="w-3 h-3 text-indigo-500 flex-shrink-0 mt-0.5" />
                            {tip}
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Red Flag */}
                    {selectedAnnotation.redFlag && (
                      <div className="mb-3 bg-red-50 rounded-lg p-3 border border-red-200">
                        <h5 className="text-xs font-semibold text-red-700 uppercase tracking-wide mb-1 flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" />
                          Red Flag
                        </h5>
                        <p className="text-xs text-red-600">{selectedAnnotation.redFlag}</p>
                      </div>
                    )}

                    {/* Benchmark */}
                    {selectedAnnotation.benchmark && (
                      <div className="mb-3 bg-green-50 rounded-lg p-3 border border-green-200">
                        <h5 className="text-xs font-semibold text-green-700 uppercase tracking-wide mb-1">
                          Market Benchmark
                        </h5>
                        <p className="text-xs text-green-600 font-mono">{selectedAnnotation.benchmark}</p>
                      </div>
                    )}

                    {/* Example */}
                    {selectedAnnotation.example && (
                      <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                        <h5 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1">
                          Example
                        </h5>
                        <p className="text-xs text-gray-600 italic">{selectedAnnotation.example}</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="p-6 text-center">
                    <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <MessageCircle className="w-6 h-6 text-gray-400" />
                    </div>
                    <h4 className="font-medium text-gray-900 mb-1">Select a Term</h4>
                    <p className="text-xs text-gray-500">
                      Click on any highlighted term in the contract to see detailed guidance, negotiation tips, and red flags.
                    </p>
                  </div>
                )}
              </div>

                {/* Quick Stats */}
                <div className="mt-4 bg-white rounded-xl border border-gray-200 p-4">
                  <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-3">
                    Key Terms in This Article
                  </h4>
                  <div className="space-y-2">
                    {contractTemplates[selectedContractType]?.sections
                      .find((s) => s.id === activeArticle)
                      ?.annotations.map((a) => (
                        <button
                          key={a.id}
                          onClick={() => setSelectedAnnotation(a)}
                          className={`w-full text-left px-3 py-2 rounded-lg text-xs transition-colors flex items-center gap-2 ${
                            selectedAnnotation?.id === a.id
                              ? 'bg-indigo-100 text-indigo-700'
                              : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                          }`}
                        >
                          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                            a.type === 'critical' ? 'bg-red-500' :
                            a.type === 'danger' ? 'bg-orange-500' :
                            a.type === 'warning' ? 'bg-yellow-500' :
                            'bg-blue-500'
                          }`}></span>
                          {a.title}
                        </button>
                      )) || (
                      <p className="text-xs text-gray-400 text-center py-2">No annotations in this section</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Disclaimer */}
            <div className="bg-gray-100 rounded-lg p-3 border border-gray-200">
              <p className="text-xs text-gray-500 text-center">
                <strong>Disclaimer:</strong> This is a simplified template for educational purposes only.
                Actual contracts are more complex. Always consult qualified legal counsel for real negotiations.
              </p>
            </div>
            </>
          )}
        </div>
      )}

      {/* Amendment Guide Section - Interactive Contract Style */}
      {activeSection === 'amendments' && (
        <div className="space-y-4">
          {/* Header */}
          <div className="bg-gradient-to-r from-amber-500 to-orange-600 rounded-xl p-6 text-white">
            <div className="flex items-center gap-3 mb-2">
              <Edit2 className="w-8 h-8" />
              <h2 className="text-xl font-bold">Contract Amendment Guide</h2>
            </div>
            <p className="text-amber-100">
              Interactive mock amendment document. Click highlighted terms to see explanations in the sidebar.
              Based on real amendments from Amgen-AstraZeneca collaboration.
            </p>
          </div>

          {/* Section Navigation */}
          <div className="bg-white rounded-xl border border-gray-200 p-2 overflow-x-auto">
            <div className="flex gap-1 min-w-max">
              {amendmentDocument.sections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => {
                    setActiveAmendmentSection(section.id);
                    setSelectedAmendmentAnnotation(null);
                  }}
                  className={`px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${
                    activeAmendmentSection === section.id
                      ? 'bg-amber-100 text-amber-700'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {section.title.replace('ARTICLE ', 'Art. ').replace(' - ', ': ')}
                </button>
              ))}
            </div>
          </div>

          {/* Main Content: Document + Sidebar */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Left: Amendment Document */}
            <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 overflow-hidden">
              {/* Document Header */}
              <div className="bg-gray-800 text-white p-4 text-center">
                <h2 className="text-lg font-bold tracking-wide">{amendmentDocument.title}</h2>
                <p className="text-sm text-gray-300">{amendmentDocument.subtitle}</p>
                <p className="text-xs text-gray-400 mt-1">{amendmentDocument.parties}</p>
              </div>

              {/* Document Content */}
              <div className="p-6 font-serif text-gray-800 leading-relaxed max-h-[600px] overflow-y-auto">
                {amendmentDocument.sections
                  .filter((s) => s.id === activeAmendmentSection)
                  .map((section) => (
                    <div key={section.id}>
                      <h3 className="font-bold text-lg mb-4 text-gray-900 border-b pb-2">
                        {section.title}
                      </h3>
                      <div className="text-sm whitespace-pre-wrap">
                        {section.annotations.length > 0 ? (
                          (() => {
                            let content = section.content;
                            let elements = [];
                            let lastIndex = 0;

                            section.annotations
                              .sort((a, b) => content.indexOf(a.term) - content.indexOf(b.term))
                              .forEach((annotation, idx) => {
                                const termIndex = content.indexOf(annotation.term, lastIndex);
                                if (termIndex !== -1) {
                                  // Add text before the term
                                  if (termIndex > lastIndex) {
                                    elements.push(
                                      <span key={`text-${idx}`}>
                                        {content.substring(lastIndex, termIndex)}
                                      </span>
                                    );
                                  }
                                  // Add the highlighted term
                                  elements.push(
                                    <button
                                      key={`term-${annotation.id}`}
                                      onClick={() => setSelectedAmendmentAnnotation(annotation)}
                                      className={`px-1 py-0.5 rounded cursor-pointer transition-colors ${
                                        selectedAmendmentAnnotation?.id === annotation.id
                                          ? 'ring-2 ring-amber-400'
                                          : ''
                                      } ${
                                        annotation.type === 'critical' ? 'bg-red-100 text-red-800 hover:bg-red-200' :
                                        annotation.type === 'danger' ? 'bg-orange-100 text-orange-800 hover:bg-orange-200' :
                                        annotation.type === 'warning' ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200' :
                                        'bg-blue-100 text-blue-800 hover:bg-blue-200'
                                      }`}
                                    >
                                      {annotation.term}
                                    </button>
                                  );
                                  lastIndex = termIndex + annotation.term.length;
                                }
                              });

                            // Add remaining text
                            if (lastIndex < content.length) {
                              elements.push(
                                <span key="text-end">{content.substring(lastIndex)}</span>
                              );
                            }

                            return elements;
                          })()
                        ) : (
                          section.content
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            </div>

            {/* Right: Annotation Sidebar */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="bg-amber-600 text-white p-3">
                <h3 className="font-semibold text-sm flex items-center gap-2">
                  <MessageCircle className="w-4 h-4" />
                  Clause Guidance
                </h3>
              </div>

              {selectedAmendmentAnnotation ? (
                <div className="p-4 space-y-3 max-h-[560px] overflow-y-auto">
                  {/* Title & Badge */}
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                        selectedAmendmentAnnotation.type === 'critical' ? 'bg-red-100 text-red-700' :
                        selectedAmendmentAnnotation.type === 'danger' ? 'bg-orange-100 text-orange-700' :
                        selectedAmendmentAnnotation.type === 'warning' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-blue-100 text-blue-700'
                      }`}>
                        {selectedAmendmentAnnotation.type === 'critical' ? 'Critical' :
                         selectedAmendmentAnnotation.type === 'danger' ? 'High Risk' :
                         selectedAmendmentAnnotation.type === 'warning' ? 'Warning' : 'Info'}
                      </span>
                    </div>
                    <h4 className="font-bold text-gray-900">{selectedAmendmentAnnotation.title}</h4>
                    <p className="text-xs text-gray-500 mt-1">{selectedAmendmentAnnotation.importance}</p>
                  </div>

                  {/* Tips */}
                  <div className="bg-green-50 rounded-lg p-3 border border-green-200">
                    <h5 className="text-xs font-semibold text-green-700 uppercase tracking-wide mb-2 flex items-center gap-1">
                      <Lightbulb className="w-3 h-3" />
                      Tips
                    </h5>
                    <ul className="space-y-1">
                      {selectedAmendmentAnnotation.tips.map((tip, i) => (
                        <li key={i} className="text-xs text-green-700 flex items-start gap-1">
                          <span className="text-green-500">•</span>
                          {tip}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Red Flag */}
                  {selectedAmendmentAnnotation.redFlag && (
                    <div className="bg-red-50 rounded-lg p-3 border border-red-200">
                      <h5 className="text-xs font-semibold text-red-700 uppercase tracking-wide mb-1 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" />
                        Red Flag
                      </h5>
                      <p className="text-xs text-red-600">{selectedAmendmentAnnotation.redFlag}</p>
                    </div>
                  )}

                  {/* Benchmark */}
                  {selectedAmendmentAnnotation.benchmark && (
                    <div className="bg-purple-50 rounded-lg p-3 border border-purple-200">
                      <h5 className="text-xs font-semibold text-purple-700 uppercase tracking-wide mb-1">
                        Market Benchmark
                      </h5>
                      <p className="text-xs text-purple-600 font-mono">{selectedAmendmentAnnotation.benchmark}</p>
                    </div>
                  )}

                  {/* Example */}
                  {selectedAmendmentAnnotation.example && (
                    <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                      <h5 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1">
                        Example / Best Practice
                      </h5>
                      <p className="text-xs text-gray-600 italic">{selectedAmendmentAnnotation.example}</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="p-6 text-center">
                  <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <MessageCircle className="w-6 h-6 text-amber-500" />
                  </div>
                  <h4 className="font-medium text-gray-900 mb-1">Select a Term</h4>
                  <p className="text-xs text-gray-500">
                    Click on any highlighted term in the amendment document to see guidance, tips, and red flags.
                  </p>
                </div>
              )}

              {/* Key Terms in This Section */}
              <div className="border-t border-gray-200 p-4">
                <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-3">
                  Key Terms in This Section
                </h4>
                <div className="space-y-2">
                  {amendmentDocument.sections
                    .find((s) => s.id === activeAmendmentSection)
                    ?.annotations.map((a) => (
                      <button
                        key={a.id}
                        onClick={() => setSelectedAmendmentAnnotation(a)}
                        className={`w-full text-left px-3 py-2 rounded-lg text-xs transition-colors flex items-center gap-2 ${
                          selectedAmendmentAnnotation?.id === a.id
                            ? 'bg-amber-100 text-amber-700'
                            : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                          a.type === 'critical' ? 'bg-red-500' :
                          a.type === 'danger' ? 'bg-orange-500' :
                          a.type === 'warning' ? 'bg-yellow-500' :
                          'bg-blue-500'
                        }`}></span>
                        {a.title}
                      </button>
                    )) || (
                    <p className="text-xs text-gray-400 text-center py-2">No annotations</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Disclaimer */}
          <div className="bg-gray-100 rounded-lg p-3 border border-gray-200">
            <p className="text-xs text-gray-500 text-center">
              <strong>Disclaimer:</strong> This is a simplified mock amendment for educational purposes.
              Based on real SEC filings from Amgen-AstraZeneca collaboration. Always consult qualified legal counsel.
            </p>
          </div>
        </div>
      )}

      {/* Exit Scenarios Section */}
      {activeSection === 'exit' && (
        <div className="space-y-4">
          {/* Header */}
          <div className="bg-gradient-to-r from-red-500 to-rose-600 rounded-xl p-6 text-white">
            <div className="flex items-center gap-3 mb-2">
              <LogOut className="w-8 h-8" />
              <h2 className="text-xl font-bold">Exit Scenarios & Termination Playbook</h2>
            </div>
            <p className="text-red-100">
              What happens when a deal ends? Learn about termination triggers, IP reversion,
              clinical trial handoffs, and financial settlements. Based on real Vigil-Amgen termination.
            </p>
          </div>

          {/* Termination Triggers */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="font-semibold text-gray-900 mb-4">Termination Triggers</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {exitScenarios.triggers.map((trigger) => (
                <div
                  key={trigger.id}
                  className={`rounded-lg p-4 border-2 ${
                    trigger.color === 'blue' ? 'bg-blue-50 border-blue-200' :
                    trigger.color === 'red' ? 'bg-red-50 border-red-200' :
                    trigger.color === 'purple' ? 'bg-purple-50 border-purple-200' :
                    'bg-orange-50 border-orange-200'
                  }`}
                >
                  <h4 className={`font-semibold mb-1 ${
                    trigger.color === 'blue' ? 'text-blue-900' :
                    trigger.color === 'red' ? 'text-red-900' :
                    trigger.color === 'purple' ? 'text-purple-900' :
                    'text-orange-900'
                  }`}>{trigger.name}</h4>
                  <p className={`text-sm mb-2 ${
                    trigger.color === 'blue' ? 'text-blue-700' :
                    trigger.color === 'red' ? 'text-red-700' :
                    trigger.color === 'purple' ? 'text-purple-700' :
                    'text-orange-700'
                  }`}>{trigger.description}</p>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    trigger.color === 'blue' ? 'bg-blue-200 text-blue-800' :
                    trigger.color === 'red' ? 'bg-red-200 text-red-800' :
                    trigger.color === 'purple' ? 'bg-purple-200 text-purple-800' :
                    'bg-orange-200 text-orange-800'
                  }`}>
                    Notice: {trigger.notice}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Termination Checklist */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="font-semibold text-gray-900 mb-4">Termination Checklist by Category</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {exitScenarios.checklist.map((category, idx) => (
                <div key={idx} className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-red-100 text-red-700 text-xs flex items-center justify-center font-bold">
                      {idx + 1}
                    </span>
                    {category.category}
                  </h4>
                  <ul className="space-y-2">
                    {category.items.map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <span className={`mt-1 w-2 h-2 rounded-full flex-shrink-0 ${
                          item.critical ? 'bg-red-500' : 'bg-gray-300'
                        }`}></span>
                        <span className={item.critical ? 'text-gray-900 font-medium' : 'text-gray-600'}>
                          {item.item}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <div className="mt-4 flex items-center gap-4 text-xs text-gray-500">
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-red-500"></span>
                Critical items
              </span>
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-gray-300"></span>
                Standard items
              </span>
            </div>
          </div>

          {/* Real Case Study */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="bg-red-600 text-white px-5 py-3">
              <h3 className="font-semibold">Real Case Study: {exitScenarios.realCase.title}</h3>
            </div>
            <div className="p-5">
              <p className="text-sm text-gray-600 mb-4 bg-gray-50 p-3 rounded-lg">
                <strong>Context:</strong> {exitScenarios.realCase.context}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {exitScenarios.realCase.keyTerms.map((item, idx) => (
                  <div key={idx} className="flex items-start gap-3 bg-gray-50 rounded-lg p-3">
                    <span className="w-6 h-6 rounded-full bg-red-100 text-red-700 text-xs flex items-center justify-center font-bold flex-shrink-0">
                      {idx + 1}
                    </span>
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase">{item.term}</p>
                      <p className="text-sm text-gray-800">{item.value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Disclaimer */}
          <div className="bg-gray-100 rounded-lg p-3 border border-gray-200">
            <p className="text-xs text-gray-500 text-center">
              <strong>Source:</strong> Vigil Neuroscience - Amgen Inc. Partial Termination Agreement (May 2025).
              SEC Filing EX-10.1. For educational purposes only.
            </p>
          </div>
        </div>
      )}

      {/* China Compliance Section */}
      {activeSection === 'china' && (
        <div className="space-y-4">
          {/* Header */}
          <div className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-xl p-6 text-white">
            <div className="flex items-center gap-3 mb-2">
              <ShieldCheck className="w-8 h-8" />
              <h2 className="text-xl font-bold">China CSO Compliance Guide</h2>
            </div>
            <p className="text-emerald-100">
              RDPAC guidance for working with Contract Sales Organizations in China.
              Covers anti-bribery compliance, due diligence, monitoring, and FCPA considerations.
            </p>
          </div>

          {/* Overview */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-start gap-4">
              <div className="w-16 h-16 bg-emerald-100 rounded-xl flex items-center justify-center text-2xl flex-shrink-0">
                🇨🇳
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">{chinaComplianceData.overview.title}</h3>
                <p className="text-sm text-gray-500">{chinaComplianceData.overview.issuer}</p>
                <p className="text-xs text-gray-400 mt-1">Published: {chinaComplianceData.overview.date}</p>
                <p className="text-sm text-gray-600 mt-2">{chinaComplianceData.overview.description}</p>
              </div>
            </div>
          </div>

          {/* Key Risks */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Key Compliance Risks with CSOs
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {chinaComplianceData.keyRisks.map((risk, idx) => (
                <div
                  key={idx}
                  className={`rounded-lg p-3 border ${
                    risk.severity === 'high' ? 'bg-red-50 border-red-200' : 'bg-yellow-50 border-yellow-200'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      risk.severity === 'high' ? 'bg-red-200 text-red-800' : 'bg-yellow-200 text-yellow-800'
                    }`}>
                      {risk.severity === 'high' ? 'High Risk' : 'Medium Risk'}
                    </span>
                  </div>
                  <h4 className={`font-semibold text-sm ${
                    risk.severity === 'high' ? 'text-red-900' : 'text-yellow-900'
                  }`}>{risk.risk}</h4>
                  <p className={`text-xs mt-1 ${
                    risk.severity === 'high' ? 'text-red-700' : 'text-yellow-700'
                  }`}>{risk.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* CSO Activities */}
          <div className="bg-emerald-50 rounded-xl border border-emerald-200 p-5">
            <h3 className="font-semibold text-emerald-900 mb-3">Typical CSO Activities</h3>
            <div className="flex flex-wrap gap-2">
              {chinaComplianceData.csoActivities.map((activity, idx) => (
                <span key={idx} className="bg-white border border-emerald-300 text-emerald-700 px-3 py-1 rounded-full text-sm">
                  {activity}
                </span>
              ))}
            </div>
          </div>

          {/* Compliance Checklist */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="font-semibold text-gray-900 mb-4">CSO Compliance Checklist</h3>
            <div className="space-y-4">
              {chinaComplianceData.checklist.map((section) => (
                <div key={section.id} className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-800 mb-3">{section.title}</h4>
                  <ul className="space-y-2 mb-3">
                    {section.items.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm">
                        <CheckSquare className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="bg-emerald-50 rounded-lg p-3 border border-emerald-200">
                    <p className="text-xs text-emerald-700">
                      <strong>💡 Tip:</strong> {section.tips}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Key Takeaways */}
          <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl border border-emerald-200 p-5">
            <h3 className="font-semibold text-emerald-900 mb-3">Key Takeaways</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="bg-white rounded-lg p-3 border border-emerald-200">
                <h4 className="font-medium text-emerald-800 text-sm mb-1">Drug Owner Liability</h4>
                <p className="text-xs text-emerald-700">
                  Under FCPA and Chinese law, you may be liable for CSO's bribery activities.
                  CSO is seen as promoting on your behalf.
                </p>
              </div>
              <div className="bg-white rounded-lg p-3 border border-emerald-200">
                <h4 className="font-medium text-emerald-800 text-sm mb-1">Due Diligence is Critical</h4>
                <p className="text-xs text-emerald-700">
                  Thorough pre-engagement review of CSO's compliance program, past violations, and leadership.
                </p>
              </div>
              <div className="bg-white rounded-lg p-3 border border-emerald-200">
                <h4 className="font-medium text-emerald-800 text-sm mb-1">Fair Market Value</h4>
                <p className="text-xs text-emerald-700">
                  Service fees must be at FMV. Use cost-plus or performance-based mechanisms with proper documentation.
                </p>
              </div>
              <div className="bg-white rounded-lg p-3 border border-emerald-200">
                <h4 className="font-medium text-emerald-800 text-sm mb-1">Active Monitoring</h4>
                <p className="text-xs text-emerald-700">
                  Regular audits, activity reviews, expense testing, and compliance hotline are essential.
                </p>
              </div>
            </div>
          </div>

          {/* Disclaimer */}
          <div className="bg-gray-100 rounded-lg p-3 border border-gray-200">
            <p className="text-xs text-gray-500 text-center">
              <strong>Source:</strong> RDPAC CSO Compliance Guidance (October 2024).
              For reference only. Consult legal counsel for specific compliance requirements.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
